// Hand-typed mirror of the `/api/v1` contract (the Zod request schemas + the
// serialized module return types). ADR-0004 calls for OpenAPI-derived types;
// until the service serves an OpenAPI document, these are maintained by hand
// — a deliberate, dep-light interim (swap to `openapi-typescript` when the
// spec endpoint lands).

/** Room lifecycle: Active / Archived / In Trash (`trashed`). `draft` is retired
 * (new rooms are created active) and `closed` is a retired alias the backend maps
 * to `archived`; both stay in the union only because the backend enum still has
 * them — neither is a user action. */
export type RoomLifecycleState = 'draft' | 'active' | 'archived' | 'closed' | 'trashed';

/**
 * The role the admin is playing FOR THIS ROOM (ADR-0010, PRD-0004 §0). Chosen
 * at setup (§3a Config), changeable later; seeds invite-time default access and
 * controls buyer-portal activation. Mirrors the Postgres `room_admin_role` enum.
 */
export type RoomAdminRole = 'intermediary' | 'buyer' | 'seller';

export interface Room {
  id: string;
  tenantId: string;
  name: string;
  lifecycleState: RoomLifecycleState;
  /**
   * The room-scoped seller principal (migration 0018) — a first-class actor
   * bound to the room, mirroring the backend `Room.sellerActorId`. Null when no
   * seller is bound. Read by the room set-up flow (skip the seller invite when one
   * is bound) and the admin's Settings › Seller panel.
   */
  sellerActorId?: string | null;
  /** Room role model (ADR-0010). Null only for rooms created before migration 0010. */
  adminRole: RoomAdminRole | null;
  /** Derived from `adminRole === 'buyer'` — the buyer portal is active iff true. */
  buyerPortalActive: boolean;
  /**
   * The per-room toggle (migration 0019) gating whether the room's owners may START
   * an LOI response (accept / counter / reject, ADR-0029) or an extension beside an
   * intermediary. Owners can always VOTE on what the intermediary proposes, and in a
   * room the buyer or the seller runs they decide whatever the toggle says. Optional
   * only for rooms serialized before the column existed — the API always sends it now.
   */
  sellerCanRespondLoi?: boolean;
  /** The room's single master NDA document (migration 0027), or null when unset.
   * Issuing an agreement defaults to this document. */
  masterNdaDocumentId?: string | null;
  /** The room's fiscal year end (migration 0057, ADR-0040): month 1–12 and the
   * month's last day (February: 28). Default December 31 = the calendar year.
   * Optional only for rooms serialized before the column existed. */
  fiscalYearEndMonth?: number;
  fiscalYearEndDay?: number;
  /** The IANA timezone the room's "today" is read in (migration 0057). Default UTC. */
  timezone?: string;
  /** Enforced exclusivity (migration 0064, ADR-0046): whether the countdown is
   * shown to the exclusive buyer / to the paused buyers. Default false. */
  exclusivityClockToBuyer?: boolean;
  exclusivityClockToOthers?: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface LoginRequest {
  /** Optional: omit for email-first login (the server resolves the tenant from
   * the email). Sent only to log in directly to a known org, or to re-submit
   * after the ambiguous-tenant picker. */
  tenantId?: string;
  email: string;
  password: string;
}

/** A tenant the signed-in email belongs to, offered by the ambiguous-tenant
 * picker (an email registered in more than one organization). */
export interface LoginTenantOption {
  tenantId: string;
  name: string;
}

export interface LoginResponse {
  /** Present on a completed login: the session still owes the TOTP challenge
   * (or, with `mfaEnrollmentRequired`, an authenticator set-up). */
  mfaRequired?: boolean;
  /** MFA is mandatory for this admin and no authenticator is set up yet: the
   * session can only reach the enrollment endpoints until it enrolls. */
  mfaEnrollmentRequired?: boolean;
  /** Present INSTEAD of `mfaRequired` when the email is registered in more than
   * one organization: the password was verified; pick one and re-submit login
   * with its `tenantId`. */
  tenants?: LoginTenantOption[];
}

/** POST /auth/code/verify — the 6-digit code emailed by POST /auth/code/request. */
export interface CodeVerifyRequest {
  email: string;
  code: string;
  /** Re-submit after the organization picker. */
  tenantId?: string;
  /** Required when the answer was `passwordRequired` (an invitee); otherwise it
   * replaces the password (a forgotten password). */
  newPassword?: string;
}

/** A completed code sign-in looks like a {@link LoginResponse}; two answers
 * instead ask for a re-submit with the same code: `passwordRequired` (choose a
 * password) and `tenants` (choose an organization). */
export interface CodeVerifyResponse extends LoginResponse {
  passwordRequired?: boolean;
}

/** POST /auth/mfa/enroll — a new authenticator secret (shown once). */
export interface MfaEnrollResponse {
  secretBase32: string;
  otpAuthUri: string;
}

/** POST /auth/mfa/enroll/confirm — one-time recovery codes (shown once). */
export interface MfaEnrollConfirmResponse {
  recoveryCodes: string[];
}

/**
 * One room the signed-in actor is a PRINCIPAL of, from GET /auth/session. A
 * room appears iff the actor is its admin (`isAdmin`) and/or its bound seller
 * (`isSeller`, migration 0018) — both can be true for a room the actor both
 * administers and sells. Rooms the actor has no principal relationship to are
 * never listed (the endpoint does not leak them).
 */
export interface SessionRoom {
  roomId: string;
  /** The room's display name, so a "Your data rooms" landing can list rooms by
   * name instead of raw ids. */
  name: string;
  isAdmin: boolean;
  isSeller: boolean;
  /** True when the actor works on the room's SELLER SIDE without being one of its
   * sellers: a live seller's-team member (ADR-0028). The seller portal lists these
   * rooms beside `isSeller` ones; nothing binding is theirs (only the sellers decide). */
  isSellerTeam: boolean;
  /** True for a seller's-team member who carries the LEGAL team role: they may see
   * the room's LOIs, their documents and their final outcome — never the votes. */
  isLegalTeam?: boolean;
  /** True when the actor reaches this room via a LIVE access grant (buyer/diligence
   * guest) rather than the admin/seller bindings. A room where all three are the
   * relevant principal kind still lists once; a guest room has isAdmin/isSeller
   * false and isMember true. */
  isMember: boolean;
  /** When the actor's access to this room runs out: the latest end of their live
   * grants. Left out when they reach the room only as admin/seller, or while any
   * live grant has no end. Feeds the buyer's first-run guide. */
  accessEndsAt?: string;
}

/**
 * GET /api/v1/auth/session — the signed-in actor's identity and the rooms they
 * are a principal of ("separate seller login" slice 2). The seller SPA uses it
 * to resolve its real room (the first {@link SessionRoom} with `isSeller`)
 * instead of a hardcoded demo stub.
 */
export interface SessionResponse {
  actorId: string;
  rooms: SessionRoom[];
}

/**
 * POST /api/v1/rooms/:roomId/seller — bind the room's SELLER principal
 * (migration 0018). Admin-only. Provide EXACTLY ONE of `actorId` (an existing
 * actor, validated in-tenant) or `email` (find-or-create the actor in the
 * tenant; the seller signs in later via the existing password+TOTP flow, which
 * resolves the same actor by the same normalized email). Returns the updated
 * {@link Room}; a non-admin caller → 404.
 */
export interface BindSellerRequest {
  actorId?: string;
  email?: string;
}

export interface ListRoomsResponse {
  rooms: Room[];
}

/** One admin of a room (GET /rooms/:roomId/admins, migration 0069): the room's
 * main admin (`isMain`) or a co-admin colleague with the same powers there. */
export interface RoomAdmin {
  actorId: string;
  email: string;
  displayName: string | null;
  isMain: boolean;
  /** When the co-admin was added; null for the main admin. */
  addedAt: string | null;
  /** Who added the co-admin; null for the main admin. */
  addedByEmail: string | null;
}

/** GET/POST/DELETE /rooms/:roomId/admins. `canManage`: the caller may add and
 * remove co-admins (the room's main admin or the firm's owner). */
export interface RoomAdminsResponse {
  admins: RoomAdmin[];
  canManage: boolean;
}

/** One room's whole-checklist counts on the rooms dashboard — the same four
 * fulfillment buckets the room's checklist adds up (ADR-0032);
 * `resolved` = covered + not applicable. */
export interface RoomChecklistFigures {
  total: number;
  resolved: number;
  covered: number;
  notApplicable: number;
  waitingOnReview: number;
  missing: number;
}

/**
 * One room on the rooms dashboard (GET /rooms/summary): plain facts, each the
 * same figure the room's own page shows. Only rooms the caller administers and
 * that are in progress are listed.
 */
export interface RoomDashboardEntry {
  roomId: string;
  checklist: RoomChecklistFigures;
  /** The Data room's "Needs review" count (low confidence + couldn't classify). */
  documentsNeedingReview: number;
  /** The Q&A badge: new questions + seller drafts to release. */
  questionsNeedingAction: number;
  /** Buyers of the room (the Activity tab's buyer table). */
  buyers: number;
  /** The latest action a buyer took themselves (opened, signed, submitted). */
  lastBuyerActivityAt: string | null;
}

export interface RoomDashboardResponse {
  rooms: RoomDashboardEntry[];
}

export interface CreateRoomRequest {
  name: string;
  /** Room role model (ADR-0010). Optional — the API defaults to `intermediary`. */
  adminRole?: RoomAdminRole;
  /** Fiscal year end month 1–12 (ADR-0040); the day is the month's last.
   * Optional — the API defaults to December (the calendar year). */
  fiscalYearEndMonth?: number;
  /** IANA timezone; optional — the API defaults to UTC. */
  timezone?: string;
}

/** PATCH /rooms/:roomId/name — rename the room. Room admin (main or co-admin)
 * only, 404 for anyone else; an archived room → 409. The name is trimmed and
 * must be 1–120 characters with no control characters (400 otherwise). Returns
 * the updated {@link Room}; the same name again is a no-op. */
export interface RenameRoomRequest {
  name: string;
}

/** PATCH /rooms/:roomId/fiscal-calendar (ADR-0040). Admin-only. Refused with a
 * 409 (`code: 'fiscal_calendar_locked'`) once the room's checklist exists. */
export interface SetRoomFiscalCalendarRequest {
  fiscalYearEndMonth: number;
  timezone?: string;
}

/** PATCH /rooms/:roomId/admin-role (ADR-0010 §1). Changing the role once grants
 * exist is advisory (warning #4); resubmit with `acknowledgeWarnings: true` to
 * proceed past a 409. */
export interface SetRoomAdminRoleRequest {
  adminRole: RoomAdminRole;
  acknowledgeWarnings?: boolean;
}

/**
 * PATCH /rooms/:roomId/seller-loi-permission (migration 0019) — admin-only. Sets
 * whether the room's bound seller principal may record the LOI responses
 * (accept / counter / reject) alongside the admin. 404 for a non-admin.
 * Returns the updated {@link Room}.
 */
export interface SetRoomSellerLoiPermissionRequest {
  sellerCanRespondLoi: boolean;
}

/** POST /rooms/:roomId/lifecycle — change the room's status. Admin-only. Only the
 * transitions the state machine allows are accepted (active→archived/trashed,
 * archived→active/trashed, trashed→active = restore); an invalid transition, or
 * one that would exceed a plan limit (`extra.code`, e.g. `archived_limit_reached`),
 * → 409. The UI only ever sends `active` | `archived` | `trashed`. */
export interface TransitionRoomLifecycleRequest {
  to: RoomLifecycleState;
}

/** The room statuses reachable from a given one, for building the UI's action
 * buttons — mirrors the server-side VALID_TRANSITIONS (rooms/index.ts). Legacy
 * `draft`/`closed` rooms have no user transitions. */
export const ROOM_LIFECYCLE_TRANSITIONS: Record<RoomLifecycleState, RoomLifecycleState[]> = {
  draft: [],
  active: ['archived', 'trashed'],
  archived: ['active', 'trashed'],
  trashed: ['active'],
  closed: [],
};

export type DocumentSource = 'upload' | 'seller_submission' | 'email_ingest' | 'migration';

/** Categorization state of a document (AI classification → reviewer approval).
 * `processing` = ingested, not yet classified; `needs_review` = AI proposed
 * tags awaiting a reviewer; `categorized` = tags approved (folder-ready). */
export type DocumentStatus = 'processing' | 'needs_review' | 'categorized';

/**
 * Confidence-based AI classification badge state, derived server-side from the
 * classification result (mirrors the backend `ClassificationStatus`,
 * src/modules/documents/classification-status.ts). Distinct from the
 * review-workflow {@link DocumentStatus}: this is the per-file signal the admin
 * upload / document list surfaces render.
 *   - `processing`     → ingested, classification not yet resolved ("Processing")
 *   - `classified`     → a category proposal cleared the confidence threshold
 *   - `low_confidence` → classified, but nothing confident ("Classified — low
 *                        confidence, please check")
 *   - `failed`         → the job could not classify the file at all, not even by
 *                        its file name ("Couldn't classify") — ADR-0018; rare,
 *                        usually the AI provider was down. An unreadable file is
 *                        classified by name and is NOT `failed`.
 *   - `not_classifiable` → never sent to AI classification by design (deal
 *                        paperwork: a buyer's LOI, a counter, the master NDA, a
 *                        signing document) and never filed; the chip names its
 *                        `paperworkKind` ("Buyer LOI", "Master NDA" …).
 *                        Not "needs review": the server refuses to re-classify it.
 * Returned by both the finalize response and the documents list.
 */
export type ClassificationStatus =
  | 'processing'
  | 'classified'
  | 'low_confidence'
  | 'failed'
  | 'not_classifiable';

/**
 * The document's end-to-end processing step (classification, then the
 * personal-data redaction pass), served to the room admin and bound seller:
 *   - `processing`      → classification running (= classificationStatus `processing`)
 *   - `ocr`             → filed; a SCAN is being read in full (OCR) on the worker —
 *                         served once the run has OCR'd at least one page, so a
 *                         text PDF (whose OCR run finishes at once) never shows it
 *   - `redaction_check` → the redaction job is queued/running on the worker
 *   - `needs_review`    → uncertain redaction marks await a person, or the
 *                         redaction could not be completed safely
 *   - `needs_filing`    → category uncertain (= classificationStatus `low_confidence`;
 *                         the document stays in the filing review queue)
 *   - `done`            → classified and redaction finished / not needed / off
 *   - `failed`          → the file could not be read (= classificationStatus `failed`)
 * Null/absent (buyers, older servers): the UI falls back to `classificationStatus`.
 */
export type ProcessingStatus =
  | 'processing'
  | 'ocr'
  | 'redaction_check'
  | 'needs_review'
  | 'needs_filing'
  | 'done'
  | 'failed';

/**
 * Per-file indicators (owner direction 2026-10-12), served to the room admin
 * and the bound seller only (buyers get null): the FILING result is the primary
 * status; OCR and redaction run in the background and show as two small
 * indicators beside it.
 *   - filingStatus:    classifying / filed / needs_filing / failed / not_filed (deal paperwork)
 *   - ocrStatus:       none (nothing to OCR) / pending / complete / failed
 *   - ocrProgress:     pages OCR'd so far of the page count, while pending (once known)
 *   - redactionStatus: off (the room's redaction is off) / pending / complete /
 *                      needs_review / unsupported / failed; null until filed
 */
export type FilingStatus = 'classifying' | 'filed' | 'needs_filing' | 'failed' | 'not_filed';
export type OcrStatus = 'none' | 'pending' | 'complete' | 'failed';
export type RedactionStatus =
  | 'off'
  | 'pending'
  | 'complete'
  | 'needs_review'
  | 'unsupported'
  | 'failed';
export interface OcrProgress {
  pagesDone: number;
  pageCount: number;
}

/** The classification states the admin should look at ("Needs review"): the
 * bulk re-classify endpoint accepts exactly these as its `status` selector. */
export type ReviewableClassificationStatus = 'low_confidence' | 'failed';

/** 202 body of POST /rooms/:roomId/documents/:documentId/reclassify (ADR-0018 §6).
 * Room admin only; the job re-runs asynchronously, so the document reads as
 * `processing` in the listing until it lands. */
export interface ReclassifyDocumentResponse {
  documentId: string;
  classificationStatus: 'processing';
}

/** Body of POST /rooms/:roomId/documents/reclassify: every document in the room
 * with that classification status (oldest first, ≤ 500), or an explicit id list
 * (≤ 500). */
export type BulkReclassifyRequest =
  | { status: ReviewableClassificationStatus }
  | { documentIds: string[] };

/** 202 body of the bulk re-classify. `truncated` means more than 500 matched —
 * call again for the rest. `notFound` lists requested ids not in this room;
 * `notReclassifiable` lists requested documents never sent to AI classification
 * (a buyer's LOI) — refused, never enqueued. */
export interface BulkReclassifyResponse {
  enqueued: number;
  documentIds: string[];
  notFound: string[];
  notReclassifiable: string[];
  truncated: boolean;
}

/**
 * Permission-scoped room document (GET /rooms/:roomId/documents). The API
 * returns only documents the caller's grant permits (choke point).
 *
 * TARGET CONTRACT (drives the backend work): `name` needs a persisted filename
 * (ingestion already receives it — a `document_versions.filename` column +
 * migration); `uploadedBy` needs a join from the current version's `created_by`
 * to the actor's email; `status` derives from classification-proposal state;
 * `categoryPath` from the document's approved category tags; `downloadReady`
 * gates the clickable name (a signed-URL viewer is a follow-up).
 *
 * `description` and `version` are PRD-0004 forward-looking fields, optional
 * until the backend serves them: `description` is the plain-language
 * explanation of the file (PRD-0004 §2.4 — a `tags.description` column,
 * surfaced per-document); `version` is the CURRENT version number, shown only
 * so the latest-version-only views can badge a replaced document "vN" (§2.2 —
 * prior versions stay hidden).
 */
/** The kind of deal paperwork (never AI-classified, never in the DD library). */
export type PaperworkKind = 'master_nda' | 'loi' | 'loi_redline' | 'signing';

export interface RoomDocument {
  id: string;
  roomId: string;
  name: string;
  uploadedBy: string | null;
  source: DocumentSource;
  status: DocumentStatus;
  /**
   * The confidence-based AI classification badge state (PR #150). The backend
   * serves it on every document row; optional here only so the demo/mock
   * datasets need not set it — real API responses always carry it.
   */
  classificationStatus?: ClassificationStatus;
  /** The document's processing step (see {@link ProcessingStatus}); when present
   * it drives the status chip. Null/absent → the chip reads `classificationStatus`. */
  processingStatus?: ProcessingStatus | null;
  /** Per-file indicators (see {@link FilingStatus}); null/absent → none (an
   * older server: read off `processingStatus`, see `fileIndicatorsOf`). */
  filingStatus?: FilingStatus | null;
  ocrStatus?: OcrStatus | null;
  ocrProgress?: OcrProgress | null;
  /** Why OCR failed — a content-free code (e.g. `timeout`, `ocr_failed`) — while `ocrStatus` is failed. */
  ocrFailureReason?: string | null;
  redactionStatus?: RedactionStatus | null;
  categoryPath: string[];
  /** Every approved `category` tag id currently on the document (the live
   * document_tags edges). `categoryPath` is the human path of the most
   * specific (deepest) of these; this is the full set, so the admin "Modify Document Attributes"
   * surface (PRD-0004 §3i) can pre-select the document's current categories.
   * Optional: served by the document listing and by search hits (both scoped
   * to the categories the viewer may see); absent in the demo/mock datasets. */
  categoryTagIds?: string[];
  /** False for a document never sent to AI classification by design (a buyer's
   * LOI): the admin UI offers no "Re-classify" for it (the server refuses it
   * anyway). Served by the document listing; absent (= true) elsewhere. */
  aiClassifiable?: boolean;
  /** Which deal paperwork a not-classifiable document is — the room's master
   * NDA, a buyer's LOI, a red-line counter, or another document issued for
   * signature — so the admin's status chip names it. Served to the room admin
   * only; null/absent otherwise. */
  paperworkKind?: PaperworkKind | null;
  /** The document's PERIOD tag ("Annual", "Quarterly", "Monthly", "YTD",
   * "TTM"), or null. Served by the document listing. */
  period?: string | null;
  /** The document's DATE tag — the end of the period its data covers, as
   * `YYYY-MM-DD` (a 2022 tax return is 2022-12-31) — or null. The admin folder
   * tree groups documents by its year. Served by the document listing. */
  dataDate?: string | null;
  /** Who set the live filing (the most specific category): the AI or a person.
   * Served to the room admin only; null when the document is not filed, or for
   * other viewers; absent on mocks. */
  filedBy?: SetBy | null;
  /** Who set {@link period}: the AI or a person. Room admin only; null when the
   * period is not set, or for other viewers; absent on mocks. */
  periodSetBy?: SetBy | null;
  /** Who set {@link dataDate}: the AI or a person. Room admin only; null when
   * the date of data is not set, or for other viewers; absent on mocks. */
  dataDateSetBy?: SetBy | null;
  /** For a contract the register has read: the date its current version was
   * fully executed (the last party's signature, YYYY-MM-DD) — the document
   * panel's "Executed <date>". Room admin only; null otherwise; absent on mocks. */
  executionDate?: string | null;
  downloadReady: boolean;
  /** Whether THIS caller may download the document: the server's own
   * `canDownloadDocument` decision (a download-allowed grant, or the room
   * admin), computed by the document listing. A UI hint only — the content
   * route re-decides and audits every `?download=1`. Absent (search hits,
   * synthetic documents) means no Download control. */
  canDownload?: boolean;
  /** BUYERS (ADR-0064): the document's redaction is not finished, so nothing of
   * it can be opened yet — shown as "Being prepared" (no name, no viewer). The
   * content route answers 409 `document_being_prepared` meanwhile. Absent/false
   * for the admin and the seller, and on mocks. */
  beingPrepared?: boolean;
  /** The room admin's per-document override (ADR-0028): hidden from the bound
   * seller / from buyers. Reported to the room admin only (false otherwise);
   * absent on mocks and search hits. */
  hiddenFromSeller?: boolean;
  hiddenFromBuyers?: boolean;
  /** The folder the file was uploaded from ("Real Estate/Leases"), or null.
   * Reported to the room admin only (null otherwise); absent on mocks. */
  folderPath?: string | null;
  /** The current version's size in bytes. Served to the room admin and the
   * bound seller only (null otherwise); absent on mocks. */
  sizeBytes?: number | null;
  /** Why the file could not be read, in one plain sentence that asks for a
   * readable copy (a locked PDF, a damaged file, a blank scan, a zip too big to
   * unpack) — while that request is open. Served to the room admin and the
   * bound seller only; null/absent otherwise. */
  unreadable?: UnreadableFlag | null;
  /** A zip or saved email whose files were each filed as their own document:
   * how many came out of it. Admin and bound seller only. `overTrialLimit`
   * (admin only): files a free trial's document cap kept out — they are added
   * when the firm upgrades. */
  archive?: { files: number; overTrialLimit?: number } | null;
  /** The zip or saved email this document came out of. Admin and bound seller only. */
  fromArchive?: { documentId: string; name: string | null } | null;
  /** A financial statement's accounting basis (ADR-0053): derived from the
   * document by the AI, or corrected by the room admin / bound seller. Null
   * when it was never asked (not a financial statement); absent on mocks. */
  statementBasis?: DocumentStatementBasis | null;
  createdAt: string;
  description?: string | null;
  version?: number;
}

/** A financial statement's accounting basis (server: classification/statement-basis.ts). */
export type StatementBasis = 'cash' | 'accrual' | 'modified_cash' | 'not_stated';

/** Who set a document's basis: the AI, from the document — or a person correcting it. */
export type StatementBasisSource = 'ai' | 'seller' | 'admin';

export interface DocumentStatementBasis {
  basis: StatementBasis;
  source: StatementBasisSource;
  /** The classifier's confidence (0-1) in its answer — AI answers, reported to
   * the room admin and the bound seller only; null otherwise. */
  confidence: number | null;
}

/** PUT /rooms/:roomId/documents/:documentId/statement-basis (admin or bound seller). */
export interface SetStatementBasisRequest {
  basis: StatementBasis;
}

export interface SetStatementBasisResponse {
  id: string;
  statementBasis: DocumentStatementBasis;
}

/** Why a file could not be read (server: documents/unreadable.ts). */
export type UnreadableReason = 'password_protected' | 'damaged' | 'no_text' | 'archive_too_large';

export interface UnreadableFlag {
  reason: UnreadableReason;
  /** The sentence to show as is, e.g. "This PDF is password-protected — please upload an unlocked copy." */
  message: string;
}

export interface ListDocumentsResponse {
  documents: RoomDocument[];
}

/**
 * PATCH /rooms/:roomId/documents/:documentId (PRD-0004 §3i "Modify Document
 * Attributes"). Admin-only. Phase A edits exactly one attribute — the full
 * desired set of `category` tag ids (an empty array clears the document's
 * categories). Renaming is not offered: the `documents` table has no
 * document-level display column (the only filename is the immutable
 * per-version `document_versions.filename`).
 */
export interface ModifyDocumentAttributesRequest {
  categoryTagIds: string[];
}

/** PATCH /rooms/:roomId/documents/:documentId/visibility (ADR-0028). Admin-only:
 * hide one document from the bound seller and/or from buyers. An omitted field
 * keeps its value. */
export interface DocumentVisibilityRequest {
  hiddenFromSeller?: boolean;
  hiddenFromBuyers?: boolean;
}

/** Result of a visibility change (200): the document's resulting override. */
export interface DocumentVisibilityResponse {
  id: string;
  hiddenFromSeller: boolean;
  hiddenFromBuyers: boolean;
}

/** Result of a successful modify (200): the document and its resulting
 * category-tag set. */
export interface ModifyDocumentAttributesResponse {
  id: string;
  roomId: string;
  categoryTagIds: string[];
  /**
   * When a person filed the document at a specific home: how many other
   * documents of its series (the same file name apart from the date) are being
   * filed the same way in the background, and how many were left alone because
   * they are already filed somewhere else (ADR-0020 update 2026-10-12).
   */
  similarDocuments?: { filing: number; conflicts: number };
}

/** Who set a document's filing, period or date of data. A person's value is
 * their decision: the AI never overwrites it afterwards. */
export type SetBy = 'ai' | 'person';

/** A document's PERIOD — only the cadence the data covers; the year/month
 * lives in the date of data. Exactly the classifier's period labels (the tags
 * are named "Period: Annual" etc.). */
export type PeriodLabel = 'Annual' | 'Quarterly' | 'Monthly' | 'YTD' | 'TTM';

/**
 * PUT /rooms/:roomId/documents/:documentId/dating. Room admin only: a person
 * sets or clears the document's period and date of data. At least one key; an
 * omitted key leaves that field unchanged, `null` clears it. Unknown keys are a
 * 400. Recorded as the person's decision (the AI never overwrites it) and any
 * pending AI suggestion for that field is closed (rejected). 409 for deal
 * paperwork or an archived room.
 */
export interface SetDocumentDatingRequest {
  period?: PeriodLabel | null;
  /** `YYYY-MM-DD` — by convention the LAST day the data covers (a 2025 annual
   * statement is 2025-12-31; Q1 2025 is 2025-03-31). */
  dataDate?: string | null;
}

/** Result of a dating change (200): the document's resulting period and date. */
export interface SetDocumentDatingResponse {
  id: string;
  roomId: string;
  period: string | null;
  dataDate: string | null;
  periodSetBy: SetBy | null;
  dataDateSetBy: SetBy | null;
}

/**
 * Request query for GET /rooms/:roomId/search (PRD-0004 §4 "find a document").
 * Mirrors the endpoint's Zod schema (src/modules/http/routes/search.ts):
 * `query` is optional free text; `tagFilter` is a category tag id or list of
 * ids the result must carry ALL of (AND across facets); `docType` is a single
 * doc-type tag id. All optional — an empty query is a pure facet browse.
 */
export interface SearchDocumentsQuery {
  query?: string;
  tagFilter?: string | string[];
  docType?: string;
}

/**
 * A single search hit. The endpoint returns permission-scoped hits in exactly the
 * document-listing shape (src/modules/search/index.ts `SearchResultRow extends
 * DocumentDetail` — the same rows GET /rooms/:roomId/documents serves, including
 * `name` and the viewer-scoped `categoryPath`), so this is a
 * {@link RoomDocument} plus `rank`: the relevance score when a free-text query
 * was given (a query matches the extracted text OR the current file name; a
 * file-name hit outranks a content-only hit — server NAME_MATCH_RANK), and
 * `null` for a pure facet browse (no query).
 */
export interface SearchResultDocument extends RoomDocument {
  rank: number | null;
}

/** GET /rooms/:roomId/search response envelope — `{ results }` (the route
 * replies `reply.code(200).send({ results })`). */
export interface SearchDocumentsResponse {
  results: SearchResultDocument[];
}

/** A node in the room's category hierarchy — the "folder tree" that is really
 * a view over the curated category tag graph (GET /rooms/:roomId/categories).
 * `documentCount` counts documents categorized under this node or its
 * descendants (permission-scoped like the document listing). */
export interface CategoryNode {
  id: string;
  name: string;
  documentCount: number;
  children: CategoryNode[];
}

/**
 * A checklist phase in the folder tree (the spreadsheet's Column A: Financial,
 * Legal, HR, Risk, Technology), holding its top-level categories in checklist
 * order. Display only (src/modules/dd/folder-order.ts).
 */
export interface CategoryFolderPhase {
  id: string;
  name: string;
  position: number;
  categoryIds: string[];
}

export interface ListCategoriesResponse {
  categories: CategoryNode[];
  /** The room checklist's phases over the categories shown, in position order;
   * absent from older servers, empty for a room without a checklist. */
  phases?: CategoryFolderPhase[];
  /** The shown categories the checklist reaches, in checklist order (parents
   * first); a category not listed has no checklist position. */
  categoryOrder?: string[];
}

/**
 * A folder's audience (ADR-0010 §2 tier) for the admin's folder tree
 * (GET /rooms/:roomId/folder-audiences → `{ folders }`). `ownTier` null = the
 * folder follows the folder it is in; `effectiveTier` is what applies.
 * `editable` is false for a firm-wide folder shared by every room.
 */
export interface FolderAudience {
  id: string;
  parentId: string | null;
  ownTier: FunnelStage | null;
  effectiveTier: FunnelStage;
  editable: boolean;
}

/** PUT /rooms/:roomId/folders/:tagId/audience — `tier` null = follow the folder
 * it is in (a folder inside another only). A 409 carries advisory `warnings`;
 * resend with `acknowledgeWarnings: true` to proceed. */
export interface SetFolderAudienceRequest {
  tier: 'teaser' | 'nda_signed' | 'due_diligence' | null;
  acknowledgeWarnings?: boolean;
}

/**
 * One category in the room's taxonomy with the seller's submission coverage
 * (GET /rooms/:roomId/coverage). `documentCount` counts the caller's VISIBLE
 * submitted documents filed under this category, rolled up from descendants
 * (same as {@link CategoryNode}'s count); `covered` is `documentCount > 0`.
 * `path` is the root-to-leaf name path (for disambiguating nested leaves).
 */
export interface CoverageCategory {
  id: string;
  name: string;
  path: string[];
  documentCount: number;
  covered: boolean;
}

/**
 * The seller submission coverage summary (GET /rooms/:roomId/coverage, PRD-0004
 * §2.3). A READ-ONLY "how far along am I" view derived from existing data — the
 * room's category taxonomy + the seller's already-submitted documents,
 * permission-scoped by the same choke point as the document listing. `categories`
 * is every taxonomy category with its coverage; `totalSubmitted` is the total
 * distinct visible documents submitted (includes any uncategorized ones, so it
 * is NOT the sum of the rolled-up per-category counts); `categoriesCovered` of
 * `categoriesTotal` have at least one document.
 */
export interface RoomCoverageResponse {
  categories: CoverageCategory[];
  totalSubmitted: number;
  categoriesTotal: number;
  categoriesCovered: number;
}

/**
 * Seller submission ingest (POST /rooms/:roomId/ingest/finalize). The submit
 * portal base64-encodes each dropped file and posts it here; the service
 * decodes, stores the bytes, creates (or versions) the document, and kicks off
 * classification. `contentType` is the browser-reported MIME type (falls back
 * to application/octet-stream when the OS gives none).
 */
export interface IngestFinalizeRequest {
  filename: string;
  contentType: string;
  contentBase64: string;
  /**
   * The file's path inside an uploaded FOLDER, file name included
   * ("Real Estate/Leases/Scan_0042.pdf") — `relativePathOf` (lib/drop-files).
   * Omitted for a loose file. The server keeps only the normalized folder, as a
   * classification clue (ADR-0018 update 2026-10-06).
   */
  relativePath?: string;
}

/** Result of a finalize call (ADR-0061: ids and status only): 201 for a new
 * document, or 200 with `skipped` when the room already held a document with
 * exactly these bytes (ADR-0028) — then nothing was stored, and `documentId` is
 * that existing document when the uploader can see it; for one they cannot (the
 * admin hid it from the seller) there is no id at all, only "already in the room". */
export interface IngestFinalizeResponse {
  documentId?: string;
  /** The new version (absent for a skipped upload). */
  versionId?: string;
  /** true when these exact bytes were already in the room: nothing was uploaded. */
  skipped: boolean;
  /**
   * The just-ingested document's confidence-based classification badge state
   * (PR #150), so the upload UI can render the per-file badge straight off the
   * 201 without a follow-up documents-list call. Null for a skipped upload (the
   * existing document's state is unchanged). Optional only so the offline mocks
   * need not set it.
   */
  classificationStatus?: ClassificationStatus | null;
}

// ---------------------------------------------------------------------------
// AI classification review queue (issue #131) — the admin's per-room queue of
// sub-threshold, still-pending document<->tag proposals. Confident category
// proposals (>=0.75) are auto-filed server-side (an approved `document_tags`
// edge, reflected in a document's `categoryPath`); the lower-confidence ones
// stay here for the admin to approve or reject. Mirrors the serialized
// `DocumentTagProposal` (src/modules/classification/proposals.ts) — `Date`s
// become ISO strings over JSON. Admin-only server-side (404 for a non-admin).
// ---------------------------------------------------------------------------

/** Review state of a proposal; the pending queue only ever returns `proposed`. */
export type ClassificationProposalState = 'proposed' | 'approved' | 'rejected';

/**
 * One AI-proposed document<->tag association awaiting review
 * (GET /rooms/:roomId/classification/pending → `{ pending }`). `tagId` resolves
 * to a category (or doc-type) tag name via the room's categories; `confidence`
 * is the classifier's [0,1] score the per-document detail view renders as a
 * percentage. `reasoning` is the classifier's short rationale, when present.
 */
export interface ClassificationProposal {
  id: string;
  tenantId: string;
  documentId: string;
  tagId: string;
  source: string;
  confidence: number;
  state: ClassificationProposalState;
  reasoning: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** The suggested tag's name and type, so the review panel can label a
   * period, date, doc-type or brand-new category suggestion. */
  tagName?: string;
  tagType?: 'category' | 'date' | 'doc_type' | 'expiration' | 'custom';
}

/** GET /rooms/:roomId/classification/pending response envelope. */
export interface ListPendingProposalsResponse {
  pending: ClassificationProposal[];
}

/** 200 body of the approve/reject endpoints
 * (POST /rooms/:roomId/classification/:proposalId/{approve,reject}). */
export interface ReviewProposalResponse {
  ok: true;
}

/** RFC-7807 problem+json body the API returns on error (src/modules/http/problem.ts).
 * Extension members (`...err.extra`) are spread at the top level — e.g. a 409
 * from the grant / role-change endpoints carries `warnings` (ADR-0010 §3). */
export interface ProblemDetails {
  status: number;
  title?: string;
  detail?: string;
  correlationId?: string;
  /** On `section_items_due` (409): how many items in the section — required or
   * optional — are unresolved. */
  unresolved?: number;
  /** On `section_items_due` (409): the required subset of `unresolved`. */
  requiredDue?: number;
  /** Advisory grant/role-change warnings on a 409 (advise + acknowledge). */
  warnings?: GrantWarning[];
  /** Stable machine code from `extra.code` (spread at the top level), e.g. a plan
   * limit (`active_limit_reached`), `billing_not_configured` (503, billing
   * dormant) or `section_items_due` (409, certifying complete too early). */
  code?: string;
  /** Plan-limit 409s only: the plan's limit, current usage, and suggested next steps. */
  limit?: number;
  current?: number;
  options?: string[];
  /** `upgrade_required` 403s only (ADR-0044): the Plus-and-Professional feature a
   * free-trial or Starter tenant asked for, e.g. `default_checklist`. */
  feature?: string;
  /** `trial_limit_reached` (ADR-0044) and `plan_limit_reached`: which cap was hit (`plan_limit_reached` is only `ask_per_day`, a paid plan's daily Ask allowance). */
  cap?: 'documents' | 'ask_per_day' | 'previews_per_minute';
  /** 429s (`trial_limit_reached`, `plan_limit_reached`, `read_rate_limited`): seconds until a retry may succeed. */
  retryAfter?: number;
  /** `invalid_checklist` 422s only: up to N row-level problems + the full count. */
  problems?: ChecklistWorkbookProblem[];
  totalProblems?: number;
  /** `document_not_visible_to_buyer` 400s only (ADR-0039, an ADMIN's answer or
   * release): the linked ids the asking buyer cannot see. */
  documentIds?: string[];
}

// ---------------------------------------------------------------------------
// Plan & billing (spec #140) — GET /tenant/plan and the owner-only
// /tenant/billing/* endpoints. Billing answers 503 `billing_not_configured`
// while the Stripe provider is dormant.
// ---------------------------------------------------------------------------

export type TenantPlanTier = 'free_trial' | 'starter' | 'buying_office' | 'professional' | 'enterprise';

/** The tiers a tenant can start a subscription checkout for. */
export type CheckoutTier = 'starter' | 'buying_office' | 'professional';

/** The three metered plan dimensions (same keys for `limits` and `usage`). */
export interface PlanCounts {
  activeRooms: number;
  archivedRooms: number;
  adminSeats: number;
}

/** The plan's limits: the three metered dimensions plus the daily "Ask the Data Room" allowance (null = unlimited). */
export interface PlanLimits extends PlanCounts {
  askPerDay: number | null;
}

/** GET /tenant/plan — tier, limits and live usage (tenant-admin-gated). */
export interface TenantPlan {
  tier: TenantPlanTier;
  isTrial: boolean;
  /** ISO timestamp; null when the tenant is not on a trial. */
  trialEndsAt: string | null;
  /** ISO timestamp (ADR-0055): when an unconverted trial's rooms move to Trash (trial end + 30 days); null when not on a trial. */
  trialRoomsTrashAt?: string | null;
  limits: PlanLimits;
  usage: PlanCounts;
  /** True on Plus, Professional and Enterprise; false on a free trial and on Starter, which
   * start from the standard checklist instead (ADR-0044). */
  includesClosingVaultChecklist: boolean;
  /** True on Professional and Enterprise: only they may SAVE a checklist for reuse (existing saved
   * checklists stay usable on every plan). */
  includesSavedChecklists: boolean;
  /** ADR-0068: the price cohort of a paid subscription ('founding' prices are kept while the firm stays subscribed); null with none. */
  priceCohort?: PriceCohort | null;
  /** ISO timestamp the firm entered that cohort. */
  priceCohortSince?: string | null;
  /** Monthly, or the 12-month commitment billed yearly; null with no paid subscription. */
  billingInterval?: BillingInterval | null;
  /** Archive packs held (each is 10 more archived rooms, already counted inside `limits.archivedRooms`). */
  archivePacks?: number;
}

/** Stable `code`s a plan-limit 409 carries (POST /rooms, /rooms/:id/lifecycle, /tenant/admins). */
export type PlanLimitCode = 'active_limit_reached' | 'archived_limit_reached' | 'seat_limit_reached';

/** Monthly, or a 12-month commitment billed yearly up front. */
export type BillingInterval = 'month' | 'year';

/** The price cohort a firm pays in (ADR-0068): the first 25 firms are 'founding'. */
export type PriceCohort = 'founding' | 'standard';

export interface BillingCheckoutRequest {
  tier: CheckoutTier;
  interval: BillingInterval;
}

/** One paid plan's prices (cents) for the firm's cohort. */
export interface BillingPlanPrices {
  tier: CheckoutTier;
  /** What a person sees (the plan coded buying_office is "Plus"). */
  name: string;
  /** Month to month, cents per month. */
  monthlyCents: number;
  /** The 12-month commitment, cents billed once a year up front. */
  yearlyCents: number;
  /** The 12-month price spread over twelve months, cents per month. */
  yearlyMonthlyCents: number;
  /** What the plan includes — read from the server's tier limits, never retyped here. */
  activeLimit: number;
  archivedLimit: number;
  seatLimit: number;
  /** "Ask the Data Room" questions a day; null = unlimited. */
  askPerDay: number | null;
}

/** The archive-pack add-on: `rooms` more archived rooms per pack, cents per month or per year (billed up front). */
export interface BillingArchivePack {
  rooms: number;
  monthlyCents: number;
  yearlyCents: number;
}

/** GET /tenant/billing/pricing — the plan picker's prices for THIS firm (any tenant admin; works while billing is dormant). */
export interface BillingPricing {
  cohort: PriceCohort;
  /** The firm holds a founding spot right now. */
  holdsFounding: boolean;
  foundingSpotsTotal: number;
  foundingSpotsLeft: number;
  /** The interval of the firm's live subscription; null with none. */
  currentInterval: BillingInterval | null;
  plans: BillingPlanPrices[];
  archivePack: BillingArchivePack;
}

/** POST /tenant/billing/archive-pack — add `packs` archive packs (owner-only; paid plan). */
export interface BillingArchivePackRequest {
  interval: BillingInterval;
  packs: number;
}

export interface BillingRecoveryRequest {
  roomId: string;
}

/** Response of every billing endpoint: the hosted Stripe URL to redirect to. */
export interface BillingUrlResponse {
  url: string;
}

// ---------------------------------------------------------------------------
// Buyer portal (ADR-0004 surface c — `web/room`)
//
// The buyer is a signed-in viewer moving through a date-gated funnel.
// The stages, the admin-issued signing envelopes, and the LOI submission below
// are the contract the buyer portal consumes; envelopes are ISSUED BY ADMIN —
// the buyer only walks (consents/signs/seals) the ones assigned to them, never
// creates them.
// ---------------------------------------------------------------------------

/**
 * The buyer's position in the date-gated acquisition funnel
 * (GET /rooms/:roomId/funnel). Ordered ascending: a buyer starts at `teaser`,
 * sealing the assigned NDA advances them past `nda_signed` to `cim` (CIM
 * unlocks), and submitting an LOI advances them to `loi_submitted`. PRD-0004
 * §1.1 added the post-LOI stages (`due_diligence` unlocks all files;
 * `exclusivity_expires` and `closing` are dated steps). Mirrors the backend
 * `funnel_stage` enum; the buyer-portal stepper renders the first four and
 * treats any later stage as "past LOI".
 */
export type FunnelStage =
  | 'teaser'
  | 'nda_signed'
  | 'cim'
  | 'loi_submitted'
  | 'due_diligence'
  | 'exclusivity_expires'
  | 'closing';

/**
 * GET /rooms/:roomId/funnel — the buyer's full funnel position. PRD-0004 §1.1
 * added the dated post-LOI steps: `exclusivityExpiresAt` and `closingDate` are
 * ISO dates (YYYY-MM-DD) once the admin sets them on advance, null before.
 */
export interface RoomFunnelResponse {
  stage: FunnelStage;
  exclusivityExpiresAt: string | null;
  closingDate: string | null;
  /** The room's mode, served on the caller's own GET /funnel: `buy_side` in a
   * BUYER-RUN room (ADR-0039) — the caller is on the buyer's own team and the
   * sell-side funnel (teaser → NDA → CIM → LOI) does not apply. Absent on the
   * admin advance/rollback responses and on older servers (= sell side). */
  roomMode?: 'sell_side' | 'buy_side';
}

/**
 * POST /rooms/:roomId/funnel/:actorId/advance (PRD-0004 §1.1/§3f) — admin-only.
 * Advances the named buyer's funnel by exactly one legal step; `date`
 * (YYYY-MM-DD) is required for the dated steps (exclusivity_expires / closing)
 * and rejected (400) for the others. 409 on an illegal step, 404 for a
 * non-admin. Returns the updated {@link RoomFunnelResponse}.
 */
export interface AdvanceFunnelRequest {
  to: FunnelStage;
  date?: string;
  /** Required (with `reason`) to move a buyer into `nda_signed` / `loi_submitted`
   * WITHOUT their signature — those stages are normally reached by the buyer
   * signing; without it the API answers 409. Audited as an override. */
  override?: boolean;
  reason?: string;
}

/**
 * POST /rooms/:roomId/funnel/:actorId/rollback (PRD-0004 §3e buyer-detail) —
 * admin-only. Moves the named buyer's funnel BACK by exactly one legal step (the
 * inverse of advance); no body. Because access is derived from the current stage
 * on every read, this also removes any higher-stage document/CIM visibility and
 * clears the dated privilege (exclusivity/closing) the left stage carried. 409 at
 * `teaser` (no predecessor), 404 for a non-admin. Returns the updated
 * {@link RoomFunnelResponse}.
 */
export type RollbackFunnelResponse = RoomFunnelResponse;

/**
 * An LOI's status (ADR-0029). Mirrors the `loi_status` enum. The seller side
 * RECORDS one response on a `submitted` LOI — `accepted`, `redlined` (shown
 * as **Countered**) or `rejected`; countered / rejected close the round and the
 * buyer may upload a fresh signed LOI. Legacy rows from the retired e-sign flow
 * may still read `accepted_signed` (shown as Accepted) or
 * `awaiting_buyer_signature` (a typed LOI the buyer never signed — not a
 * submission; nothing can be recorded on it).
 */
export type LoiStatus =
  | 'awaiting_buyer_signature'
  | 'submitted'
  | 'accepted'
  | 'rejected'
  | 'accepted_signed'
  | 'redlined';

/** The seller side's three recordable responses (ADR-0029). */
export type LoiDecision = 'accept' | 'counter' | 'reject';

/**
 * The exclusivity terms entered when accepting an LOI (ADR-0046): the day it
 * starts (YYYY-MM-DD, in the room's timezone — today or later, within a year)
 * and its length in days (1–365).
 */
export interface ExclusivityTerms {
  startDate: string;
  lengthDays: number;
}

/** A member of the exclusive buyer's team — shown to the ADMIN only. */
export interface ExclusivityTeamMember {
  actorId: string;
  email: string | null;
}

/**
 * One person on a buyer's NAMED team (migrations 0070 + 0075): colleagues and
 * outside advisers of any email domain. They see what the lead buyer sees once
 * they have signed the NDA themselves, and keep access with the lead during an
 * exclusivity. Only the lead buyer and the room admin ever see this.
 */
export interface BuyerTeamMember {
  actorId: string;
  email: string;
  /** Their team roles (Legal, Financial, …); none = []. */
  roles?: TeamRole[];
  /** The intermediary only — always null for the lead buyer. */
  displayName: string | null;
  addedByRole: 'admin' | 'buyer';
  addedAt: string;
  /** Has signed this room's NDA themselves. */
  ndaSigned: boolean;
  /** Their team access is live. */
  hasAccess: boolean;
}

/** A lead buyer's ask to add someone — the intermediary approves or declines. */
export interface BuyerTeamRequest {
  id: string;
  email: string;
  status: 'pending' | 'declined';
  requestedAt: string;
  /** The roles the buyer named for the person. */
  roles?: TeamRole[];
}

/** A waiting ask as the ADMIN sees it: whose, and why it can't be approved (if so). */
export interface AdminBuyerTeamRequest extends BuyerTeamRequest {
  leadActorId: string;
  leadEmail: string;
  problem: string | null;
  /** The address already has an account (approving re-invites it). */
  existingAccount: boolean;
}

/** GET /rooms/:roomId/team — the signed-in buyer's OWN team and asks. */
export interface MyBuyerTeamResponse {
  members: BuyerTeamMember[];
  requests: BuyerTeamRequest[];
  /** Whether this buyer may ask for / remove people. */
  canManage: boolean;
  /** This buyer is on another buyer's team (only that buyer manages it). */
  memberOfTeam: boolean;
  /** Why they cannot manage the team, in plain words; null when they can. */
  reason: string | null;
}

/** GET /rooms/:roomId/buyers/:actorId/team — ADMIN: a buyer's team, whose team they are on, their asks. */
export interface AdminBuyerTeamResponse {
  members: BuyerTeamMember[];
  memberOf: { actorId: string; email: string }[];
  requests: AdminBuyerTeamRequest[];
}

/** GET /rooms/:roomId/team-requests — ADMIN: the room's waiting asks. */
export interface ListTeamRequestsResponse {
  requests: AdminBuyerTeamRequest[];
}

/** POST /rooms/:roomId/team (buyer ask) and /rooms/:roomId/buyers/:actorId/team (admin add). */
export interface AddBuyerTeamMemberRequest {
  email: string;
}

/** POST /rooms/:roomId/team → 202: the ask, always the same answer. */
export interface RequestBuyerTeamMemberResponse {
  request: BuyerTeamRequest;
}

/** Admin add / approve → 201. */
export interface AddBuyerTeamMemberResponse {
  member: BuyerTeamMember;
}

/**
 * POST /rooms/:roomId/loi/:loiId/decision — the room admin, or a seller while the
 * room's `sellerCanRespondLoi` toggle is on, records the response to a submitted
 * LOI. No signature, no counter document — a status of record. An ACCEPT must
 * carry `exclusivity` (ADR-0046). A SELLER's response is a VOTE in the sellers'
 * ownership-weighted majority (ADR-0046 amendment, 2026-10-22): it takes effect the
 * moment sellers holding more than 50% approve (at once for a single seller at
 * 100%), otherwise the LOI stays `submitted` and the other sellers are asked to
 * vote (`sellerVote` carries the tally). The intermediary's accept waits for the
 * sellers' vote. 409 for an already-decided LOI, while another exclusivity is
 * live, while the sellers' ownership does not total 100%, or (counter / reject)
 * while this LOI's acceptance awaits validation; 400 for bad terms; 404 for an
 * unpermitted actor (the seller's team included) or a missing LOI. Returns the
 * LOI (+ `exclusivity` on an accept, + `sellerVote` when it went to a vote).
 */
export interface LoiDecisionRequest {
  decision: LoiDecision;
  exclusivity?: ExclusivityTerms;
}

/** An exclusivity's status (migration 0064). */
export type ExclusivityStatus = 'pending_validation' | 'active' | 'ended' | 'declined';
export type ExclusivityEndReason = 'ended_early' | 'closed' | 'lapsed' | 'declined';

/**
 * One exclusivity (ADR-0046) as the admin / seller see it. `expiresOn` is the
 * first day NOT covered; the period ends at 00:00 that day in `timezone`, the
 * instant `endsAt`. `inEffect` = active and not yet ended — other buyers paused.
 */
export interface Exclusivity {
  id: string;
  roomId: string;
  loiId: string;
  buyerActorId: string;
  status: ExclusivityStatus;
  startDate: string;
  lengthDays: number;
  expiresOn: string;
  endsAt: string;
  timezone: string;
  /** The seller pinned at acceptance as the only validator; null when none. */
  validatorActorId: string | null;
  acceptedBy: string;
  acceptedByRole: 'intermediary' | 'seller';
  acceptedAt: string;
  validatedBy: string | null;
  validatedAt: string | null;
  effectiveAt: string | null;
  pendingExtensionDays: number | null;
  extensionRequestedBy: string | null;
  extensionRequestedAt: string | null;
  endedAt: string | null;
  endedBy: string | null;
  endReason: ExclusivityEndReason | null;
  inEffect: boolean;
}

/** The decision response: the LOI, plus its exclusivity on an accept. */
export interface LoiDecisionResponse extends LoiSubmission {
  exclusivity?: Exclusivity;
  /** The sellers' vote so far, when the response went to a vote. */
  sellerVote?: SellerVoteTally | null;
}

/** Who the viewer is to the room's exclusivity. */
export type ExclusivityViewerRole = 'admin' | 'seller' | 'exclusive_buyer' | 'other_buyer';

/**
 * GET /rooms/:roomId/exclusivity — the room's exclusivity as the viewer may see
 * it. Admin / seller: the live record (pending or in effect), the buyer's email,
 * the clock settings and the countdown. A buyer: only whether one is in effect,
 * whether it pauses them, and the countdown when the room shows it to them.
 */
export interface ExclusivityView {
  viewerRole: ExclusivityViewerRole;
  state: 'none' | 'pending_validation' | 'in_effect';
  paused: boolean;
  clockVisible: boolean;
  endsAt: string | null;
  expiresOn: string | null;
  timezone: string;
  exclusivity: Exclusivity | null;
  buyerEmail: string | null;
  /** Who is on the winning buyer's team (keeps access) — the ADMIN only. */
  team: ExclusivityTeamMember[] | null;
  /** How many are on the winning buyer's team — admin and seller (no identities). */
  teamSize: number | null;
  /** The viewer is one of the sellers, who vote on the live exclusivity's pending decisions. */
  viewerIsValidator: boolean;
  settings: { clockToBuyer: boolean; clockToOthers: boolean } | null;
  sellerValidationRequired: boolean;
  /** The sellers' vote on the pending acceptance / extension — admin and sellers only. */
  sellerVote?: SellerDecision | null;
}

/** POST /rooms/:roomId/exclusivity/validation and …/extension/validation — the SELLER only. */
export interface ExclusivityValidationRequest {
  decision: 'validate' | 'decline';
}
export interface ExclusivityValidationResponse {
  loi: LoiSubmission;
  exclusivity: Exclusivity;
  /** The tally after this vote. */
  sellerVote?: SellerVoteTally;
}

/** POST /rooms/:roomId/exclusivity/extension — the admin or the seller (never a buyer). */
export interface ExtendExclusivityRequest {
  additionalDays: number;
}

/** PATCH /rooms/:roomId/exclusivity-settings — admin-only. Returns the Room. */
export interface SetExclusivitySettingsRequest {
  clockToBuyer?: boolean;
  clockToOthers?: boolean;
}

/**
 * POST /rooms/:roomId/loi — the buyer submits their LOI by UPLOADING THE SIGNED
 * DOCUMENT (ADR-0029), inline base64 like ingest/finalize (50 MiB cap, any file
 * type). Allowed from `cim` (or `loi_submitted` after a countered / rejected
 * round). Advances the funnel to `loi_submitted`. 201 with the LOI; 200 when the
 * same file was already submitted and is pending (a replay); 409 while an LOI is
 * pending or accepted, or from the wrong stage.
 */
export interface SubmitLoiRequest {
  filename: string;
  contentType: string;
  contentBase64: string;
}

/** An LOI as the API returns it (submit / decision responses). */
export interface LoiSubmission {
  id: string;
  roomId: string;
  actorId: string;
  submittedAt: string;
  status: LoiStatus;
  decidedAt: string | null;
  decidedBy: string | null;
  /** The uploaded signed LOI; null only on a pre-document legacy row. */
  documentId: string | null;
  /** The uploaded file's name; null when there is no document. */
  filename: string | null;
}
export type SubmitLoiResponse = LoiSubmission;

/**
 * GET /rooms/:roomId/loi — the BUYER's own latest LOI (`{ loi: null }` when they
 * have none). The document opens read-only through {@link loiDocumentContentPath}.
 */
export interface BuyerLoiState {
  id: string;
  status: LoiStatus;
  submittedAt: string;
  decidedAt: string | null;
  documentId: string | null;
  filename: string | null;
}
export interface BuyerLoiResponse {
  loi: BuyerLoiState | null;
}

/**
 * GET /rooms/:roomId/loi/:loiId/document/content — an LOI's signed document,
 * view-only. Served to the LOI's own buyer and to the room's admin / bound
 * seller; everyone else gets 404. The LOI is never in the general library.
 */
export function loiDocumentContentPath(roomId: string, loiId: string): string {
  return `/api/v1/rooms/${roomId}/loi/${loiId}/document/content`;
}

/**
 * One row of the LOI review listing (GET /rooms/:roomId/lois — the room admin or
 * its bound seller): an LOI with its status and the submitting buyer's funnel
 * position (stage + the dated post-LOI steps that establish exclusivity), plus
 * the resolved buyer email. Dates are ISO (YYYY-MM-DD) or null.
 */
export interface LoiReviewItem extends Omit<LoiSubmission, 'roomId'> {
  /** The final outcome in three words (or "Pending") — what every list shows. */
  outcome?: LoiOutcome;
  /** The sellers' open vote on this LOI (acceptance / counter / decline) — the room
   * ADMIN and the SELLERS only; never sent to a seller's-team member. */
  sellerVote?: SellerDecision | null;
  actorEmail: string | null;
  /** The buyer's display name (person or firm) — served to the room admin only
   * (null for the seller); optional for older payloads. */
  actorName?: string | null;
  funnelStage: FunnelStage;
  exclusivityExpiresAt: string | null;
  closingDate: string | null;
  /** The LOI's latest exclusivity (ADR-0046) — pending, in effect or over; null
   * when it was never accepted with one. Optional for older payloads. */
  exclusivity?: Exclusivity | null;
  /** The submitter's named team — who keeps access with them if this LOI is
   * accepted. The room ADMIN only (null for the seller); optional for older payloads. */
  team?: ExclusivityTeamMember[] | null;
  /** How many people are on the submitter's team (admin and seller). */
  teamSize?: number;
}

/**
 * One LOI as a seller's-team member with the LEGAL role sees it: the document and
 * the FINAL OUTCOME only — no status detail, no exclusivity, no votes.
 */
export interface LoiOutcomeItem {
  id: string;
  roomId: string;
  actorEmail: string | null;
  submittedAt: string;
  decidedAt: string | null;
  documentId: string | null;
  filename: string | null;
  outcome: LoiOutcome;
}

export interface ListLoisResponse {
  lois: LoiReviewItem[];
}

/** GET /rooms/:roomId/lois as a seller's LEGAL team member receives it. */
export interface ListLoiOutcomesResponse {
  lois: LoiOutcomeItem[];
}

/** An LOI's final outcome in the words everyone sees. */
export type LoiOutcome = 'Approved' | 'Declined' | 'Redlined' | 'Pending';

// ---------------------------------------------------------------------------
// Sellers, ownership and the sellers' vote (migration 0105, ADR-0028 / ADR-0046
// amendment of 2026-10-22)
// ---------------------------------------------------------------------------

/** One seller of a room and their share of the company. */
export interface RoomSeller {
  actorId: string;
  email: string | null;
  displayName: string | null;
  /** 0 < pct <= 100, at most three decimals. */
  ownershipPct: number;
  /** The earliest-added seller (the Q&A seat). */
  isPrimary: boolean;
}

/** GET /rooms/:roomId/sellers — the admin and the owners only. */
export interface RoomSellersResponse {
  sellers: RoomSeller[];
  totalPct: number;
  /** The owners together hold more than 50% — otherwise nothing can be approved. */
  canApprove: boolean;
  /** The plain warning to show while `canApprove` is false; null otherwise. */
  warning: string | null;
}

/** POST /rooms/:roomId/sellers — the admin adds a seller (or re-weights one). */
export interface AddSellerRequest {
  actorId?: string;
  email?: string;
  /** Optional only for the room's first seller (100%). */
  ownershipPct?: number;
}

/** PUT /rooms/:roomId/sellers/ownership — every owner's percentage, totalling at most 100. */
export interface SetSellerOwnershipRequest {
  sellers: { actorId: string; ownershipPct: number }[];
}

export type SellerVoteChoice = 'approve' | 'decline';
/** What a decision is about: an LOI acceptance, an exclusivity extension, or a counter / decline. */
export type SellerVoteKind = 'acceptance' | 'extension' | 'loi_response';
/** What Approve does. */
export type SellerVoteAction = 'accept' | 'counter' | 'reject' | 'extend';

/** One seller's standing in one open decision. */
export interface SellerVoteLine {
  actorId: string;
  email: string | null;
  displayName: string | null;
  ownershipPct: number;
  vote: SellerVoteChoice | null;
}

/** The count: Approve ownership above 50% approves; Decline ownership of 50% or more declines. */
export interface SellerVoteTally {
  sellers: SellerVoteLine[];
  approvedMilli: number;
  declinedMilli: number;
  totalMilli: number;
  /** The owners together hold more than 50% (else nothing can be approved). */
  canApprove: boolean;
  approvedPct: number;
  declinedPct: number;
  totalPct: number;
}

/** One decision waiting for the sellers' vote — the room admin and the sellers see it. */
export interface SellerDecision {
  kind: SellerVoteKind;
  subjectId: string;
  loiId: string;
  action: SellerVoteAction;
  proposedBy: string | null;
  proposedByRole: 'seller' | 'intermediary';
  /** The LOI's sender. */
  buyerEmail: string | null;
  /** Accept: the exclusivity terms on the table. */
  startDate: string | null;
  lengthDays: number | null;
  /** Extend: the days on the table. */
  additionalDays: number | null;
  sellers: SellerVoteLine[];
  approvedPct: number;
  declinedPct: number;
  totalPct: number;
  /** The owners together hold more than 50% (else nothing can be approved). */
  canApprove: boolean;
  /** The plain warning to show while `canApprove` is false; null otherwise. */
  warning: string | null;
  viewerVote: SellerVoteChoice | null;
  /** The viewer is an owner who may vote on this now. */
  viewerCanVote: boolean;
}

/** POST /rooms/:roomId/loi/:loiId/seller-vote — a seller votes on a proposed counter / decline. */
export interface LoiSellerVoteRequest {
  vote: SellerVoteChoice;
}

// ---------------------------------------------------------------------------
// Team roles (migration 0105): the first piece of a future tagging system. Only
// Legal has an effect today (a seller's-team member who is Legal sees the LOIs).
// ---------------------------------------------------------------------------

/** THE list of team roles — the service holds the same list (rooms/team-roles.ts). */
export const TEAM_ROLES = [
  'financial',
  'legal',
  'risk',
  'hr',
  'technical',
  'operations',
] as const;
export type TeamRole = (typeof TEAM_ROLES)[number];

export const TEAM_ROLE_LABELS: Record<TeamRole, string> = {
  financial: 'Financial',
  legal: 'Legal',
  risk: 'Risk',
  hr: 'HR',
  technical: 'Technical',
  operations: 'Operations',
};

/** PUT /rooms/:roomId/team-roles/:actorId — set (or, empty, clear) a team member's roles. */
export interface SetTeamRolesRequest {
  roles: TeamRole[];
}
export interface SetTeamRolesResponse {
  actorId: string;
  roles: TeamRole[];
}

/** Kind of agreement an envelope wraps: non-disclosure or non-circumvention. */
export type SigningEnvelopeType = 'nda' | 'nc';

/**
 * Lifecycle of an e-sign envelope as the buyer walks it. `created` = issued by
 * admin, awaiting the buyer; `consent_recorded` = buyer agreed to sign
 * electronically; `signed` = buyer applied their signature; `sealed` = the
 * signed artifact is finalized (tamper-evident) and the funnel advances.
 */
export type SigningEnvelopeStatus = 'created' | 'consent_recorded' | 'signed' | 'sealed';

/**
 * An ADMIN-ISSUED signing envelope assigned to the buyer
 * (GET /rooms/:roomId/signing/envelopes). `documentId` references the room
 * document the envelope wraps. Native e-sign sits behind a provider interface
 * (ADR), so this shape is provider-neutral.
 */
export interface SigningEnvelope {
  id: string;
  envelopeType: SigningEnvelopeType;
  status: SigningEnvelopeStatus;
  documentId: string;
  createdAt: string;
  /** Set when the admin cancelled (voided) it before sealing: never actionable.
   * The signer-scoped list omits cancelled envelopes; admin lists carry it. */
  cancelledAt?: string | null;
}

export interface ListSigningEnvelopesResponse {
  envelopes: SigningEnvelope[];
}

/** POST /rooms/:roomId/signing/envelopes/:id/consent — the e-sign consent the
 * buyer agreed to (ESIGN/UETA electronic-records consent). */
export interface RecordConsentRequest {
  consentText: string;
}

/** POST /rooms/:roomId/signing/envelopes/:id/sign — the signer's typed full
 * name, the signature text of record (required; trimmed, 1–200 characters).
 * Stored on the envelope and in the signed + sealed evidence. */
export interface SignEnvelopeRequest {
  signatureName: string;
}

/**
 * GET /rooms/:roomId/signing/envelopes/:envelopeId/document/content — the
 * document an envelope asks its signer to sign, view-only. Served to the
 * envelope's signer (and the room admin) regardless of the signer's tag grants,
 * so a teaser-stage buyer can read the NDA before signing; 404 for anyone else.
 */
export function envelopeDocumentContentPath(roomId: string, envelopeId: string): string {
  return `/api/v1/rooms/${roomId}/signing/envelopes/${envelopeId}/document/content`;
}

/* POST /rooms/:roomId/signing/envelopes/:id/seal takes NO body: the server seals
 * and hashes the stored document version itself (client bytes are ignored). */

// ---------------------------------------------------------------------------
// Admin control plane — Access Rights (PRD-0004 §3b) + grant warnings
// (ADR-0010 §2/§3). Mirrors src/modules/access + src/modules/http/routes/access.ts.
// ---------------------------------------------------------------------------

/**
 * The party role an actor is added to a room with (the access grant's
 * `access_role`). `*_team` are the admin's own side (broad default access);
 * `diligence_team` is the buyer/counterparty side (funnel-gated, teaser-only
 * default); `custom` seeds no default — its grants are always explicit.
 */
export type AccessRole = 'seller_team' | 'intermediary_team' | 'diligence_team' | 'custom';

/** The advisory warning triggers (ADR-0010 §3): grants, a room-role change, and
 * a folder's audience moved earlier. */
export type GrantWarningCode =
  | 'counterparty_logs_access'
  | 'ahead_of_funnel_stage'
  | 'all_files_before_loi'
  | 'role_change_with_grants'
  | 'role_change_qa_mode'
  | 'folder_audience_earlier'
  | 'folder_audience_earlier_checklist';

/** One advisory warning returned by a grant / role-change endpoint. Advisory,
 * not an error: the admin acknowledges it and the action proceeds. */
export interface GrantWarning {
  code: GrantWarningCode;
  message: string;
}

/**
 * An access grant as the API serializes it (GET /rooms/:roomId/access-grants →
 * `{ grants }`; also the body of a create/invite response). Mirrors the
 * backend `AccessGrant` (src/modules/access/authorization.ts) after JSON
 * serialization — `Date`s become ISO strings. NOTE: the current serializer does
 * NOT include the grantee `actorId` or party `role`, so the list is rendered by
 * scope + gating rather than by party (see the PR's owner-review note).
 */
export interface AccessGrant {
  id: string;
  /** Category tag ids this grant scopes (expanded to their subtrees server-side). */
  tagAreaScope: string[];
  /** Category tag ids excluded from the scope. */
  excludedTagIds: string[];
  downloadAllowed: boolean;
  writeAllowed?: boolean;
  /** ISO timestamp the grant becomes valid, or null (valid immediately). */
  startsAt: string | null;
  /** ISO timestamp the grant expires, or null (no expiry). */
  expiresAt: string | null;
  /** True for a standing grant with no expiry. */
  ongoing: boolean;
  /** Minimum funnel stage required for the grant to apply; null = not gated. */
  funnelStage: FunnelStage | null;
  /** ISO timestamp of explicit revocation, or null/absent when live. */
  revokedAt?: string | null;
  /** The grantee actor id (present on the room's grant-list response). */
  actorId?: string;
  /** The grantee's party role, for a human label (list response). */
  role?: AccessRole;
  /** The grantee's email from the room roster, or null (list response). */
  actorEmail?: string | null;
  /** The grantee's team roles (Legal, Financial, …) (list response). */
  teamRoles?: TeamRole[];
}

export interface ListAccessGrantsResponse {
  grants: AccessGrant[];
}

/** POST /rooms/:roomId/access-grants/invite — add a party with the role's
 * invite-time default access (ADR-0010 §2). Admin-only. Provide EXACTLY ONE of
 * `email` (a new or known person — the server find-or-creates the actor in the
 * tenant; they are emailed an invitation and sign in with an emailed code) or
 * `actorId` (an existing room member). Email is the front door for bringing a new buyer/seller into a room. */
export interface InvitePartyRequest {
  email?: string;
  actorId?: string;
  role: AccessRole;
  /** Team roles for a seller's-team or buyer-side invitee (Legal, Financial, …). */
  teamRoles?: TeamRole[];
  /** When the access ends (ISO instant). Required by the server for a buyer's
   * diligence team — buyer access is never ongoing; optional for own-side roles. */
  expiresAt?: string;
}

/** 201 body of the invite endpoint — the invited actor (created or found by
 * email) and the default grant(s) created (possibly []). */
export interface InvitePartyResponse {
  invitee?: { id: string; email: string };
  grants: AccessGrant[];
}

/**
 * POST /rooms/:roomId/access-grants — the advanced "add grant" form. Fires the
 * ADR-0010 §3 advisory warnings; on a 409 (`warnings` present, not
 * acknowledged) resubmit with `acknowledgeWarnings: true`.
 */
export interface CreateAccessGrantRequest {
  actorId: string;
  role: AccessRole;
  tagAreaScope: string[];
  excludedTagIds?: string[];
  downloadAllowed?: boolean;
  writeAllowed?: boolean;
  startsAt?: string;
  expiresAt?: string;
  ongoing?: boolean;
  funnelStage?: FunnelStage | null;
  acknowledgeWarnings?: boolean;
}

/** 201 body of the create-grant endpoint — the created grant plus any warnings
 * that fired but were acknowledged (surfaced so the admin sees them even on a
 * successful proceed). */
export interface CreateAccessGrantResponse extends AccessGrant {
  warnings: GrantWarning[];
}

/** 200 body of POST /access-grants/:grantId/revoke. */
export interface RevokeGrantResponse {
  ok: true;
}

/**
 * PATCH /rooms/:roomId/access-grants/:grantId/expiry (PRD-0004 §3e buyer-detail)
 * — admin-only. Update ONE grant's time bound: EITHER make it ongoing
 * (`ongoing: true`, which clears the expiry) OR set a new `expiresAt` (a future
 * ISO date). Exactly one of the two. A grant not in the room -> 404, a non-admin
 * -> 404, a REVOKED grant -> 409, a past/absent expiry -> 400. `ongoing: true` is
 * refused (400) for a buyer or a buyer's team: an extension is always a new date.
 * Returns the updated {@link AccessGrant}.
 */
export interface UpdateGrantExpiryRequest {
  ongoing?: boolean;
  expiresAt?: string;
}

// ---------------------------------------------------------------------------
// Admin — Logs (PRD-0004 §3d). A read-only view over the append-only,
// hash-chained audit_log. Admin-only server-side (GET /rooms/:roomId/audit-log);
// the log is immutable and view-only for everyone, including the admin, so there
// is no mutation shape. Mirrors src/modules/http/routes/audit.ts.
// ---------------------------------------------------------------------------

/** What kind of entry it is: a person's act, a view (a read), or ClosingVault acting
 * on its own. The feed shows only `person` entries unless asked for everything. */
export type AuditEntryKind = 'person' | 'view' | 'system';

/** What an entry is about, by its CURRENT name (resolved when read — the log itself
 * stores only ids). `deleted` = the object no longer exists. */
export interface AuditEntryObject {
  type: 'document' | 'item' | 'milestone' | 'folder' | 'person' | 'risk' | 'question';
  name: string | null;
  deleted: boolean;
}

/**
 * One audit-log entry as the API serializes it (newest first). `id` is the
 * BIGSERIAL sequence (a string; also the chain position). `entryHash` is the
 * SHA-256 chain hash — not secret, exposed so the UI can show tamper-evidence.
 * `actorId`/`actorEmail` are null for system events; fields are deliberately
 * minimal (no IP/user-agent/content).
 */
export interface AuditLogEntry {
  id: string;
  actorId: string | null;
  actorEmail: string | null;
  action: string;
  kind: AuditEntryKind;
  targetType: string;
  targetId: string;
  /** The thing it is about by name; null when the entry has no object we resolve (the room itself). */
  object: AuditEntryObject | null;
  /** For "provided a document for a checklist item": the document (the item is `object`). */
  related?: { type: 'document'; name: string | null } | null;
  createdAt: string;
  entryHash: string;
}

export interface ListAuditLogResponse {
  entries: AuditLogEntry[];
  /** Pass as `?before=` for the next, older page; null on the oldest page.
   * Optional for older servers (one page only). */
  nextBefore?: string | null;
}

/** The server-side filters of GET /rooms/:roomId/audit-log (all optional, ANDed). */
export interface AuditLogFilters {
  /** Also return views and system activity (default: only what people did). */
  includeAll?: boolean;
  /** Contains-match on who did it, or who it was about. */
  person?: string;
  /** Contains-match on the object's name, per kind. */
  document?: string;
  item?: string;
  milestone?: string;
  folder?: string;
  /** Free text: a person or any name — or an action in `qActions`. */
  q?: string;
  /** Exact action codes (the wording the person typed, matched to codes). */
  actions?: string[];
  qActions?: string[];
  /** Instants: `from` inclusive, `to` exclusive. */
  from?: string;
  to?: string;
}

/**
 * §3c NDA management (admin): a room-wide signing envelope — the buyer
 * {@link SigningEnvelope} plus who it was issued to. Served by the admin-only
 * GET /rooms/:roomId/signing/envelopes/all; the buyer's own listing stays
 * {@link SigningEnvelope} (no signer). `signerEmail` is the email captured on
 * the envelope at issuance.
 */
export interface AdminSigningEnvelope extends SigningEnvelope {
  signerActorId: string;
  signerEmail: string | null;
  /** Set once the admin has cancelled (voided) this envelope; null otherwise. A
   * cancelled envelope can never seal or advance the buyer's funnel. */
  cancelledAt: string | null;
}

export interface ListAdminSigningEnvelopesResponse {
  envelopes: AdminSigningEnvelope[];
}

/**
 * POST /rooms/:roomId/signing/envelopes/:id/cancel (admin) — voids an envelope
 * that has NOT yet been sealed, so it can never seal or advance the buyer's
 * funnel. Idempotent (cancelling an already-cancelled envelope just returns its
 * existing `cancelledAt`). Admin-only; a non-admin/absent/cross-room envelope →
 * 404. A SEALED envelope → 409 (sealing is final).
 */
export interface CancelEnvelopeResponse {
  envelopeId: string;
  cancelledAt: string;
}

/**
 * POST /rooms/:roomId/signing/envelopes (admin) — issue an NC/NDA envelope to a
 * buyer against a room document (PRD-0004 §3c). The signer walks it
 * (consent → sign → seal); sealing an NDA advances their funnel and unlocks the
 * CIM. Admin-only; a non-admin, an unknown document, or an unknown signer → 404.
 */
export interface IssueEnvelopeRequest {
  /** Optional — one master NDA per room, so the UI no longer picks a type; the
   * server defaults to 'nda'. */
  envelopeType?: SigningEnvelopeType;
  documentId: string;
  signerActorId: string;
}

/** PATCH /rooms/:roomId/master-nda — set (a document uuid) or clear (null) the
 * room's single master NDA document. */
export interface SetMasterNdaRequest {
  documentId: string | null;
}

/**
 * §3e CIM management (admin): one buyer in the room's funnel and what their
 * stage has unlocked — the NDA gate, the CIM, and full due-diligence. The §3c
 * parallel for the CIM ("who can currently see the CIM"). Served by the
 * admin-only GET /rooms/:roomId/buyers; `actorEmail` is resolved server-side.
 */
export interface BuyerAccessItem {
  actorId: string;
  actorEmail: string | null;
  /** The buyer's display name (person or firm), as the pipeline shows it; optional for older payloads. */
  actorName?: string | null;
  stage: FunnelStage;
  ndaSigned: boolean;
  cimUnlocked: boolean;
  allFilesUnlocked: boolean;
  /** ISO expiry of the buyer's nearest time-limited grant, or null when ongoing. */
  accessExpiresAt: string | null;
}

export interface ListBuyersResponse {
  buyers: BuyerAccessItem[];
}

// ---------------------------------------------------------------------------
// Admin — buyer engagement (ADR-0038). Plain facts and counts read off the
// room's audit log: no score, no judgment. Admin-only server-side. Mirrors
// src/modules/engagement + src/modules/http/routes/engagement.ts.
// ---------------------------------------------------------------------------

/** A buyer whose room access has ended — every buyer-side grant revoked
 * (`'revoked'`, when any was) or run out (`'expired'`). Null while any grant is
 * live or still to start. Such a buyer stays listed, with their history. */
export type BuyerAccessEnded = 'revoked' | 'expired' | null;

/** One buyer's row in the room-level comparison (`GET /rooms/:roomId/buyers/engagement`).
 * Every buyer of the room is listed — an invited buyer who has not signed the
 * NDA yet too (stage `teaser`). */
export interface BuyerEngagementSummary {
  actorId: string;
  actorEmail: string | null;
  /** The buyer's display name (person or firm) when one is on record; the
   * email stands in otherwise. Optional: older servers omit it. */
  actorName?: string | null;
  stage: FunnelStage;
  accessEnded: BuyerAccessEnded;
  /** Latest room action the buyer took themselves (opened, signed, submitted). */
  lastActiveAt: string | null;
  /** Latest completed sign-in to the platform. */
  lastSignInAt: string | null;
  /** Latest document open (view or download). */
  lastOpenedAt: string | null;
  /** Distinct library documents opened at least once. */
  documentsOpened: number;
  /** In-app views (including office previews). */
  views: number;
  downloads: number;
  /** Library documents this buyer can open today. */
  visibleDocuments: number;
  /** How many of `visibleDocuments` they have opened. */
  openedOfVisible: number;
  /** Whole percent; null when nothing is visible to them. */
  openedPercent: number | null;
}

export interface ListBuyerEngagementResponse {
  buyers: BuyerEngagementSummary[];
}

export interface BuyerDocumentEngagement {
  documentId: string;
  name: string | null;
  /** Root-to-leaf folder names; empty when the document is not filed. */
  categoryPath: string[];
  views: number;
  downloads: number;
  lastOpenedAt: string;
  /** Page-level reading (ADR-0056) when the viewer reported any; optional:
   * older servers omit it. */
  pages?: DocumentPageStats | null;
}

/** One bar of the per-page chart (ADR-0056): a page, or an equal run of pages
 * in a long document — the server sends at most 60. */
export interface PageSegment {
  firstPage: number;
  lastPage: number;
  /** Whole seconds on screen, summed over the run. */
  seconds: number;
  /** At least one page of the run was on screen for a second. */
  viewed: boolean;
}

/** One buyer's page reading of one document (ADR-0056): facts only, bounded. */
export interface DocumentPageStats {
  /** False when only an earlier version was read. */
  versionIsCurrent: boolean;
  /** The version's page count, pinned by the server. */
  pageCount: number;
  /** Pages viewed (on screen) for at least a second. */
  pagesViewed: number;
  seconds: number;
  lastReadAt: string;
  segments: PageSegment[];
}

/** `GET/PUT /rooms/:roomId/activity-alerts` — the calling admin's own "buyer
 * opened X" email (ADR-0056). */
export interface ActivityAlertSettings {
  enabled: boolean;
  /** At most one email every this many minutes. */
  intervalMinutes: number;
}

export interface ActivityAlertSettingsView extends ActivityAlertSettings {
  intervalChoices: number[];
}

/** `GET/PUT /rooms/:roomId/new-document-alerts` — the calling buyer's own
 * daily new-document email (ADR-0056). */
export interface NewDocumentAlertPreference {
  enabled: boolean;
}

/** Opens rolled up to the top-level category area (`areaId` null = not filed). */
export interface BuyerAreaEngagement {
  areaId: string | null;
  areaName: string | null;
  documentsOpened: number;
  views: number;
  downloads: number;
  lastOpenedAt: string;
}

export interface BuyerMilestones {
  stage: FunnelStage;
  ndaSignedAt: string | null;
  loiSubmittedAt: string | null;
  loiStatus: LoiStatus | null;
  loiDecidedAt: string | null;
  /** `YYYY-MM-DD` calendar dates. */
  exclusivityExpiresAt: string | null;
  closingDate: string | null;
}

/** `GET /rooms/:roomId/buyers/:actorId/engagement`. */
export interface BuyerEngagementDetail {
  summary: BuyerEngagementSummary;
  milestones: BuyerMilestones;
  /** Most recently opened first. */
  documents: BuyerDocumentEngagement[];
  /** Most documents opened first. */
  areas: BuyerAreaEngagement[];
}

// ---------------------------------------------------------------------------
// Admin — Due-Diligence request tracker (PRD-0004 §3f). Per-stage requests with
// deadlines the admin tracks to completion. Admin-only server-side. Mirrors
// src/modules/dd + src/modules/http/routes/dd.ts.
// ---------------------------------------------------------------------------

export type DdStatus = 'open' | 'completed';

/** A checklist item's fulfillment (ADR-0030): `covered` = a document is linked
 * (by a person, or a confident AI match) or the admin completed it;
 * `waiting_on_review` = nothing linked, but the AI matched a document it is not
 * sure about, or one whose filing is in review; `missing` = nothing either way. */
/**
 * Where a checklist item stands (ADR-0030, ADR-0032) — exactly one of four. Only
 * `covered` (a document) and `not_applicable` (an attested "does not apply") are
 * resolved; an OPTIONAL item that is neither is `missing` like a required one.
 */
export type DdFulfillmentStatus = 'covered' | 'not_applicable' | 'waiting_on_review' | 'missing';

/** Who attested an item's "does not apply", in which capacity, and when
 * (migration 0051). `statementKey` names the statement confirmed. */
export interface DdNotApplicableAttestation {
  statementKey: string;
  attestedBy: string | null;
  /** Always 'seller': only the room's bound seller can make the statement (owner
   * ruling 2026-10-05, ADR-0032 §5), so only theirs resolves an item. */
  attestedByRole: 'seller';
  attestedAt: string;
}

/** One document linked to a checklist item, and who linked it. */
export interface DdRequestLink {
  documentId: string;
  source: 'ai' | 'manual';
}

/**
 * A Due-Diligence request (GET /rooms/:roomId/dd-requests → `{ requests }`).
 * Attached to a funnel `stage` and carrying an optional `dueDate` deadline
 * (YYYY-MM-DD). "Overdue" is derived client-side (dueDate past AND status open).
 * `completedAt`/`createdAt` are ISO strings.
 */
export interface DdRequest {
  id: string;
  roomId: string;
  /** Nullable going forward: the phase grouping supersedes the funnel-stage
   * grouping for the checklist, but the column stays for back-compat. */
  stage: FunnelStage | null;
  title: string;
  detail: string | null;
  /** The item's deadline: its own due date, else its phase's completion date. */
  dueDate: string | null;
  /** True when `dueDate` is the phase's completion date rather than one set on the item. */
  dueDateInherited?: boolean;
  /** The admin accept/waive OVERRIDE — distinct from derived `provided`. */
  status: DdStatus;
  /** The phase this request belongs to, or null if ungrouped. */
  phaseId: string | null;
  /** DERIVED fulfillment (migration 0021): true iff the request has >=1 linked
   * document OR `status === 'completed'`. Linking a document flips it with no
   * admin write; the `status` toggle stays as the accept/waive override. */
  provided: boolean;
  /** The ATTESTED "does not apply to my company" resolution (migrations 0022 +
   * 0051): true iff someone confirmed the statement for this item. A
   * not-applicable item is resolved with no document; clearing it returns the
   * item to missing. */
  notApplicable: boolean;
  /** Who attested the "does not apply" and when; null when not marked. Absent
   * from older servers. */
  notApplicableAttestation?: DdNotApplicableAttestation | null;
  /** Whether the INTERMEDIARY requires this item (migration 0023). true (the DD
   * default) = counts toward "still due" until resolved; false = OPTIONAL, still
   * shown but never due/blocking. Set per Data Room via
   * PATCH /dd-requests/:requestId/required (admin-only). */
  required: boolean;
  /** Ids of the documents linked to this request, ordered by link time. */
  linkedDocumentIds: string[];
  /** The subset of `linkedDocumentIds` the AI matched to this item on its own
   * (ADR-0030) — shown as "Matched automatically". Absent from older servers. */
  autoLinkedDocumentIds?: string[];
  /** Every link with who made it: `ai` (matched automatically) or `manual`. */
  links?: DdRequestLink[];
  /** Documents the AI suggests for this item that a person should confirm. */
  reviewDocumentIds?: string[];
  /** Linked documents we could not read: they do not answer the item, and the
   * seller is asked for a readable copy. Absent from older servers. */
  unreadableDocumentIds?: string[];
  /** Where the item stands — server-derived; absent from older servers. */
  fulfillmentStatus?: DdFulfillmentStatus;
  /**
   * The nested category-tag PATH (root → leaf names) this item is filed under,
   * within its phase (the nested-category-hierarchy feature) — e.g.
   * `["Tax Docs", "Federal (IRS)"]`. Resolved server-side from the item's
   * `category_tag_id` by walking the tag graph; `[]` when the item has no category
   * (ungrouped, directly under the phase). The checklist UIs build the phase's
   * category tree from this.
   */
  categoryPath: string[];
  /** The item's `category_tag_id` (null = ungrouped). The section-certification
   * key is formed from THIS (`${phaseId}:${categoryTagId}`, lib/section.ts) — never
   * from a /categories lookup, which is permission-filtered for the seller. */
  categoryTagId: string | null;
  /**
   * The item sits directly under an account PLACEHOLDER ("[Bank Account]",
   * ADR-0037): it stands for "one category per account" rather than one
   * document. It is covered once {@link accounts} is non-empty, else missing (or
   * the seller's attested "does not apply", as for any item). Absent from older
   * servers.
   */
  accountPlaceholder?: boolean;
  /** The real accounts identified under this item's placeholder, each with its
   * own category. Empty for every other item; absent from older servers. */
  accounts?: DdRequestAccount[];
  /**
   * The item sits directly under the STATE placeholder ("[State]", issue #157):
   * it stands for "one category per state the company operates in". Covered once
   * {@link states} is non-empty. Absent from older servers.
   */
  statePlaceholder?: boolean;
  /** The states chosen for this item's placeholder, each with its own category. */
  states?: DdRequestState[];
  completedAt: string | null;
  createdAt: string;
}

/** A chosen state with its own category beside a "[State]" placeholder item. */
export interface DdRequestState {
  /** USPS code ("OH", or "DC"). */
  code: string;
  name: string;
  tagId: string;
}

/** A roster account with its own category under a placeholder item's category
 * (ADR-0037). Only the last 4 digits of its number are ever sent. */
export interface DdRequestAccount {
  /** The roster account id (dd-accounts). */
  id: string;
  /** The account's own category tag under this placeholder. */
  tagId: string;
  /** Its name, e.g. "Chase Operations Account". */
  label: string;
  /** The last four digits of its number, or null. */
  last4: string | null;
}

/**
 * GET /rooms/:roomId/dd-requests → the PAGED list (slice 4). A room can hold 600+
 * requested items, so the read is always paged: `requests` is one slice and
 * `total` is the count under the same `?phaseId=&status=` filters (for the FE
 * paginator), with `limit`/`offset` echoed back. Admin-or-seller server-side with
 * seller release-gating (a seller sees only released-phase requests). */
export interface ListDdRequestsResponse {
  requests: DdRequest[];
  total: number;
  limit: number;
  offset: number;
}

/** Optional query for the paged DD list. `limit` is validated to [1, 200]
 * (default 50) and `offset` to >=0 server-side (out-of-range → 400). */
export interface ListDdRequestsQuery {
  phaseId?: string;
  status?: DdStatus;
  limit?: number;
  offset?: number;
}

/**
 * One entry of the per-phase progress rollup
 * (GET /rooms/:roomId/dd-requests/summary → `{ phases }`, slice 4) — the PRIMARY
 * progress surface for the checklist. `total` is the phase's request count,
 * `provided` how many are provided (status completed OR a document linked),
 * `notApplicable` how many the seller marked "does not apply" (migration 0022),
 * `requiredTotal` how many are REQUIRED (migration 0023), and `due` how many
 * count toward "still due" — an item is due iff it is REQUIRED AND NOT resolved
 * (resolved = provided OR notApplicable). An OPTIONAL (required=false) unresolved
 * item is neither due nor blocking, and a resolved item is never due, so
 * `due = requiredTotal - DISTINCT(required AND resolved)` and is never negative.
 * `total`/`provided`/`notApplicable` are reported for context; the FE shows
 * "X of requiredTotal required complete". The admin also receives a
 * phaseless/legacy bucket with a null `phaseId`/`name`/`position`; a seller's
 * rollup covers released phases only.
 */
export interface DdPhaseSummary {
  phaseId: string | null;
  name: string | null;
  position: number | null;
  /** Effectively released — manually released OR the trigger date has arrived. */
  released: boolean;
  /** Scheduled trigger date (YYYY-MM-DD, migration 0026), or null. Always null for
   * the phaseless/legacy bucket. */
  releaseOn: string | null;
  /** The phase's start: the day it was released, else its scheduled `releaseOn`.
   * Null for the phaseless bucket. Absent from older servers. */
  startOn?: string | null;
  /** The day everything in the phase is due (YYYY-MM-DD), or null. Items without a
   * due date of their own inherit it. Absent from older servers. */
  completeOn?: string | null;
  total: number;
  provided: number;
  /** Items with an attested "does not apply" (status `not_applicable`). */
  notApplicable: number;
  requiredTotal: number;
  /** REQUIRED items unresolved — kept for compatibility; the owner-rule count is
   * `unresolved` (required AND optional). */
  due: number;
  /** Unresolved items waiting on a person's review of an AI match (ADR-0030). */
  waitingOnReview: number;
  /** Items covered by a document (status `covered`). */
  covered: number;
  /** Items with nothing — required OR optional (status `missing`). */
  missing: number;
  /** `missing + waitingOnReview`: what must reach 0 to certify complete. */
  unresolved: number;
}

/** Room-level sums of the per-phase buckets (exactly the phases returned). The
 * four buckets partition `total`: covered + notApplicable + waitingOnReview +
 * missing. */
export interface DdChecklistTotals {
  total: number;
  covered: number;
  notApplicable: number;
  waitingOnReview: number;
  missing: number;
  unresolved: number;
  requiredTotal: number;
  due: number;
}

export interface DdRequestsSummaryResponse {
  phases: DdPhaseSummary[];
  /** Absent from older servers. */
  totals?: DdChecklistTotals;
}

/**
 * POST /rooms/:roomId/dd-requests/:requestId/documents — link a document to a
 * requested item (admin OR bound seller). DELETE .../documents/:documentId
 * unlinks it. Both return the updated {@link DdRequest} (with `provided` and
 * `linkedDocumentIds` recomputed).
 */
export interface LinkDdRequestDocumentRequest {
  documentId: string;
}

/** POST /rooms/:roomId/dd-requests — create a DD request (admin-only). */
export interface CreateDdRequestRequest {
  stage: FunnelStage;
  title: string;
  detail?: string;
  dueDate?: string;
  phaseId?: string;
  /** Whether the intermediary requires the item (migration 0023). Omitted →
   * required (the DD default). */
  required?: boolean;
  /** Optional nested category PATH (root → leaf) to file the item under within
   * its phase (the feature). The nested `category` tags are found-or-created and
   * the item's `category_tag_id` is set to the LEAF; omitted → ungrouped. */
  categoryPath?: string[];
}

/** PATCH /rooms/:roomId/dd-requests/:requestId — toggle status (admin-only). */
export interface SetDdRequestStatusRequest {
  status: DdStatus;
}

/**
 * PATCH /rooms/:roomId/dd-requests/:requestId — edit an item (admin-only,
 * checklist redesign slice 7). Send only what changes, at least one field; it may
 * go with a `status`. `dueDate` is the item's OWN date (null: it inherits its
 * phase's due date again); `phaseId` moves it to another phase of the room;
 * `categoryPath` re-files it (found-or-created; [] = ungrouped). Links, required
 * and the seller's "doesn't apply" are untouched. Returns the updated item.
 */
export interface UpdateDdRequestRequest {
  status?: DdStatus;
  title?: string;
  detail?: string | null;
  dueDate?: string | null;
  phaseId?: string;
  categoryPath?: string[];
}

/** PATCH /rooms/:roomId/dd-phases/:phaseId — rename a phase (admin-only). A name
 * another phase of the room has is a 409. Returns the updated {@link DdPhase}. */
export interface RenameDdPhaseRequest {
  name: string;
}

/**
 * PATCH /rooms/:roomId/dd-requests/:requestId/required — set the INTERMEDIARY
 * per-item "required" flag per Data Room (migration 0023, admin-only). An
 * OPTIONAL (required=false) item is still shown but never counts toward "still
 * due". Returns the updated {@link DdRequest} (with `required` reflecting the
 * toggle).
 */
export interface SetDdRequestRequiredRequest {
  required: boolean;
}

/**
 * PATCH /rooms/:roomId/dd-requests/:requestId/na — set/clear the attested "does
 * not apply to my company" resolution (migrations 0022 + 0051). The room's BOUND
 * SELLER only — the admin (broker) and anyone else get 404 (ADR-0032 §5). Marking
 * REQUIRES `attest: true` (the seller confirmed NA_ATTESTATION_TEXT) — else 400
 * `attestation_required`. Returns the updated {@link DdRequest}.
 */
export interface SetDdRequestNotApplicableRequest {
  notApplicable: boolean;
  attest?: boolean;
}

/**
 * POST /rooms/:roomId/dd-requests/import — BULK IMPORT (slice 3, admin-only). Load
 * hundreds of requested items across phases in one call. The body is EITHER the
 * grouped {@link DdImportPhase} tree OR a flat delimited `rows` list (split on '/',
 * first segment = phase name, remainder = item title). Phases are found-or-created
 * by (room, name) and requests by (room, phase, title), so a re-import is
 * idempotent. Caps: at most {@link DD_IMPORT_MAX_PHASES} phases and
 * {@link DD_IMPORT_MAX_ITEMS} total items; over-cap/empty → 400.
 */
export const DD_IMPORT_MAX_ITEMS = 2000;
export const DD_IMPORT_MAX_PHASES = 100;

/** One requested item in a bulk-import phase (grouped form). */
export interface DdImportItem {
  title: string;
  detail?: string;
  /** ISO date (YYYY-MM-DD). */
  dueDate?: string;
  stage?: FunnelStage;
  /** Whether the intermediary requires the item (migration 0023). Omitted →
   * required (the DD default). */
  required?: boolean;
}

/**
 * A CATEGORY group within a bulk-import phase (the nested-category-hierarchy
 * feature): items nested under a category `path` (root → leaf). `items` are
 * EITHER a bare title string (the canonical DD-template shape) OR a full
 * {@link DdImportItem}. Each item's `category_tag_id` is set to the path's LEAF
 * (nested `category` tags found-or-created server-side).
 */
export interface DdImportCategory {
  path: string[];
  items: (string | DdImportItem)[];
}

/** One phase and its requested items in a bulk-import (grouped form). A phase may
 * carry a flat `items` list and/or `categories` (nested under a path), plus
 * `questions` (accepted so the canonical template parses; managed via the
 * phase-questions flow, migration 0024). */
export interface DdImportPhase {
  name: string;
  items?: DdImportItem[];
  categories?: DdImportCategory[];
  questions?: string[];
}

/** The bulk-import body: grouped `phases` OR flat delimited `rows` (not both). */
export type ImportDdRequestsRequest = { phases: DdImportPhase[] } | { rows: string[] };

/** 200 response: how many phases/requests were newly created vs. reused. */
export interface ImportDdRequestsResponse {
  phasesCreated: number;
  phasesReused: number;
  requestsCreated: number;
  requestsReused: number;
}

/**
 * POST /rooms/:roomId/dd-requests/instantiate-default — INSTANTIATE the built-in
 * DEFAULT template (Sweetview's real DD checklist) into the room (admin-only). A
 * ONE-TIME seed: it creates the template's phases, nested category tags, requested
 * items — with period-relative GENERATORS (annual/quarterly/monthly request sets)
 * expanded to concrete per-period requests stamped with real years/months at
 * instantiation — and each phase's free-text questions. No request body. A room
 * that already has phases → 409 conflict (this cannot seed a non-empty room).
 * Returns the created counts.
 */
export interface InstantiateDefaultTemplateResponse {
  phasesCreated: number;
  categoriesCreated: number;
  requestsCreated: number;
  questionsCreated: number;
}

// ---------------------------------------------------------------------------
// Admin — DD checklist SOURCES (ADR-0017, migration 0039). A room's checklist is
// seeded once from the built-in default, a tenant SAVED checklist, or an uploaded
// .xlsx in the Sweetview template format. Saved checklists are tenant-level config
// (`/dd-templates`, tenant-admin only → 403 otherwise). Mirrors
// src/modules/http/routes/dd-templates.ts + the instantiate routes in dd.ts.
// ---------------------------------------------------------------------------

/** POST /rooms/:roomId/dd-requests/instantiate — seed from the ClosingVault
 * `default` (Plus and Professional only: 403 `upgrade_required` otherwise), the
 * `standard` checklist (the default of a free trial and of Starter, ADR-0044) or a
 * saved checklist. 409 if the room already has phases; `saved` needs tenant admin (403). */
export type InstantiateChecklistRequest =
  | { source: 'default' }
  | { source: 'standard' }
  | { source: 'saved'; templateId: string };

/** The created counts every seeding route returns. */
export type InstantiateChecklistResponse = InstantiateDefaultTemplateResponse;

/** The upload fields shared by every checklist-workbook endpoint (base64 JSON,
 * mirroring /ingest/finalize). */
export interface ChecklistUploadRequest {
  filename?: string;
  contentType?: string;
  contentBase64: string;
}

/** POST /rooms/:roomId/dd-requests/instantiate-xlsx — seed from an uploaded
 * workbook; `saveAs` also saves it as a tenant checklist in the same transaction. */
export interface InstantiateChecklistXlsxRequest extends ChecklistUploadRequest {
  saveAs?: string;
}

/** A tenant saved checklist (no body) with its derived counts. */
export interface SavedChecklistTemplate {
  id: string;
  name: string;
  createdAt: string;
  createdBy: string;
  phaseCount: number;
  categoryCount: number;
  requestCount: number;
  questionCount: number;
  /** False when the stored body no longer passes validation: it is listed (so it
   * can be seen and deleted) with zero counts and `invalidReason`, but it can
   * never seed a room. Absent on older responses = valid. */
  valid?: boolean;
  invalidReason?: string;
}

export interface InstantiateChecklistXlsxResponse extends InstantiateChecklistResponse {
  savedTemplate: SavedChecklistTemplate | null;
}

/** GET /dd-templates. */
export interface ListSavedChecklistsResponse {
  templates: SavedChecklistTemplate[];
}

/** POST /dd-templates → 201 {@link SavedChecklistTemplate}. */
export interface CreateSavedChecklistRequest extends ChecklistUploadRequest {
  name: string;
}

/** One row-level problem in an uploaded checklist workbook (422
 * `code: 'invalid_checklist'`). Plain English; never carries cell contents. */
export interface ChecklistWorkbookProblem {
  sheet: string | null;
  row: number;
  column: string | null;
  message: string;
}

// ---------------------------------------------------------------------------
// Admin — requested-documents checklist PHASES (migration 0020). A phase is an
// ordered bucket of requested items; the intermediary RELEASES a phase (stamps
// `releasedAt`) to make it reach the seller. Admin-only server-side. Mirrors
// src/modules/dd + src/modules/http/routes/dd.ts.
// ---------------------------------------------------------------------------

/**
 * A checklist phase (GET /rooms/:roomId/dd-phases → `{ phases }`). Ordered by
 * `position`; `releasedAt` is null until released, then an ISO string. `createdAt`
 * is an ISO string.
 */
export interface DdPhase {
  id: string;
  roomId: string;
  name: string;
  position: number;
  releasedAt: string | null;
  /** Scheduled trigger date (YYYY-MM-DD, migration 0026), or null for none. */
  releaseOn: string | null;
  /** The phase's start: the day it was released, else its scheduled `releaseOn`. */
  startOn?: string | null;
  /** The day everything in the phase is due (YYYY-MM-DD), or null. */
  completeOn?: string | null;
  /** Manually released OR the trigger date has arrived — the seller-visible state. */
  effectivelyReleased: boolean;
  createdAt: string;
}

export interface ListDdPhasesResponse {
  phases: DdPhase[];
}

/** POST /rooms/:roomId/dd-phases — create a phase (admin-only). `position` is
 * auto-assigned (next in the room) when omitted. */
export interface CreateDdPhaseRequest {
  name: string;
  position?: number;
}

/** PATCH /rooms/:roomId/dd-phases/:phaseId/release-date — set (YYYY-MM-DD) or
 * clear (null) a phase's scheduled trigger date (admin-only). */
export interface SetDdPhaseReleaseDateRequest {
  releaseOn: string | null;
}

/** PATCH /rooms/:roomId/dd-phases/:phaseId/dates — set a phase's date range in one
 * write. `releaseOn` is the START (scheduled release), `completeOn` the day
 * everything in the phase is due; each is YYYY-MM-DD, null to clear, or left out
 * to keep. Send at least one. A completion before the start is a 400. */
export interface SetDdPhaseDatesRequest {
  releaseOn?: string | null;
  completeOn?: string | null;
}

/** DELETE /rooms/:roomId/dd-phases/:phaseId — what becomes of a phase's items. */
export type DeleteDdPhaseItems = { action: 'move'; toPhaseId: string } | { action: 'delete' };

export interface DeleteDdPhaseResponse {
  ok: true;
  movedItems: number;
  deletedItems: number;
  deletedQuestions: number;
}

// ---------------------------------------------------------------------------
// Admin + seller — PHASE QUESTIONS (migration 0024). The "Phase Questions" form:
// alongside the requested-documents checklist, a phase can carry free-text
// QUESTIONS the intermediary defines; the seller answers them with free text.
// Separate from dd_requests. Mirrors src/modules/dd + src/modules/http/routes/dd.ts.
// ---------------------------------------------------------------------------

/**
 * A phase question (GET /rooms/:roomId/dd-questions → `{ questions }`). Belongs to
 * a phase; the seller answers `prompt` with free text. `answer` is null until
 * answered (then `answeredBy`/`answeredAt` are stamped). `answeredAt`/`createdAt`
 * are ISO strings. Admin-or-seller server-side with seller release-gating (a
 * seller sees only released-phase questions).
 */
export interface DdPhaseQuestion {
  id: string;
  roomId: string;
  phaseId: string;
  prompt: string;
  position: number;
  /** The seller's free-text answer, or null if unanswered. */
  answer: string | null;
  /** The actor who answered (attribution), or null when unanswered. */
  answeredBy: string | null;
  answeredAt: string | null;
  /**
   * Set when the question is an account ROSTER (ADR-0037): its prompt names one
   * account kind's token ("List every [Bank Account] …"). It is answered by the
   * account list (the dd-accounts routes), not by the free-text `answer`. Null
   * for every other question; absent from older servers.
   */
  rosterKind?: DdAccountKind | null;
  /**
   * Set when the question is the STATE roster (issue #157): its prompt names
   * "[State]". It is answered by choosing states (the dd-states routes), each of
   * which gets its own categories with that state's real forms. Absent from older
   * servers.
   */
  stateRoster?: boolean;
  createdAt: string;
}

/** GET /rooms/:roomId/dd-questions → the phase questions (ordered by phase
 * position then question position), each with its answer. */
export interface ListDdPhaseQuestionsResponse {
  questions: DdPhaseQuestion[];
}

/** Optional query for the questions list: filter to a single phase. */
export interface ListDdPhaseQuestionsQuery {
  phaseId?: string;
}

/** POST /rooms/:roomId/dd-phases/:phaseId/questions — create a question under a
 * phase (admin-only). `position` is auto-assigned (next in the phase) when
 * omitted. */
export interface CreateDdPhaseQuestionRequest {
  prompt: string;
  position?: number;
}

/** PATCH /rooms/:roomId/dd-questions/:questionId — edit a question's prompt
 * and/or position (admin-only). At least one field is required. */
export interface UpdateDdPhaseQuestionRequest {
  prompt?: string;
  position?: number;
}

/** PATCH /rooms/:roomId/dd-questions/:questionId/answer — set/clear a question's
 * answer (admin OR bound seller). An empty `answer` clears it back to unanswered.
 * Returns the updated {@link DdPhaseQuestion}. */
export interface AnswerDdPhaseQuestionRequest {
  answer: string;
}

// ---------------------------------------------------------------------------
// DD ACCOUNT ROSTER (ADR-0037, migration 0054) — the room's real accounts. Each
// account gets its own category (and checklist items) under every placeholder
// of its kind. Admin or seller (a seller sees only the kinds released to them);
// merge is admin-only. Only the LAST 4 digits of a number are ever returned,
// except by the explicit, audited reveal-number call.
// ---------------------------------------------------------------------------

/** The kinds of account a checklist expands per account. */
export type DdAccountKind =
  | 'bank_account'
  | 'credit_card'
  | 'line_of_credit'
  | 'loan'
  | 'merchant_account';

/** One account kind present in the room's checklist (GET /dd-accounts `kinds`). */
export interface DdAccountKindSpec {
  kind: DdAccountKind;
  /** The placeholder token in a checklist path or roster question ("[Bank Account]"). */
  token: string;
  /** "bank account" */
  noun: string;
  /** "Bank accounts" */
  plural: string;
  /** What the institution is called for this kind ("Bank", "Issuer", "Lender"). */
  institutionNoun: string;
  /** Whether a FULL number may be stored (never for a credit card). */
  fullNumberAllowed: boolean;
  /** `institution_friendly` = "<Institution> <Friendly name>"; `friendly` = the
   * friendly name alone. */
  labelStyle: 'institution_friendly' | 'friendly';
  /** The placeholder categories of this kind (each account gets a category beside each). */
  placeholders: { tagId: string; path: string[] }[];
}

/** One of an account's own categories, beside the placeholder it expands. */
export interface DdAccountCategory {
  placeholderTagId: string;
  tagId: string;
  path: string[];
}

/** A roster account (GET /dd-accounts `accounts`). */
export interface DdAccount {
  id: string;
  kind: DdAccountKind;
  /** Its name, e.g. "Chase Operations Account" — also its categories' name. */
  label: string;
  institution: string | null;
  nickname: string | null;
  /** The last four digits of the number — the only part ever shown. */
  last4: string | null;
  /** A full number is on file (encrypted); reveal it only on an explicit click. */
  hasFullNumber: boolean;
  /** Found by the AI in a document, or added by a person. */
  source: 'ai' | 'manual';
  createdAt: string;
  categories: DdAccountCategory[];
}

/** A state (or DC) a room's business can operate in. */
export interface DdJurisdiction {
  code: string;
  name: string;
}

/** A state on the room's roster, with its categories (one per "[State]" placeholder). */
export interface DdRoomState {
  id: string;
  code: string;
  name: string;
  createdAt: string;
  categories: { placeholderTagId: string; tagId: string; path: string[] }[];
}

/** GET /rooms/:roomId/dd-states (issue #157). */
export interface ListDdStatesResponse {
  /** The 50 states and DC, by name. */
  jurisdictions: DdJurisdiction[];
  /** The room's "[State]" placeholders — none for a checklist without per-state items. */
  placeholders: { tagId: string; path: string[] }[];
  states: DdRoomState[];
}

/** PUT /rooms/:roomId/dd-states — the full set of chosen states (USPS codes). */
export interface SetDdStatesRequest {
  states: string[];
}

/** 200: the roster after the change, and what changed. 409 when a removed state
 * still has documents filed under it (nothing changes). */
export interface SetDdStatesResponse {
  states: DdRoomState[];
  added: string[];
  removed: string[];
}

/** GET /rooms/:roomId/dd-accounts */
export interface ListDdAccountsResponse {
  /** Whether full account numbers can be stored (field encryption configured). */
  fullNumbersEnabled: boolean;
  kinds: DdAccountKindSpec[];
  accounts: DdAccount[];
}

/** POST /rooms/:roomId/dd-accounts — at least one of institution / nickname /
 * last4 / accountNumber. `accountNumber` is the FULL number (digits; spaces or
 * hyphens allowed), refused for a credit card or while full numbers are off. */
export interface CreateDdAccountRequest {
  kind: DdAccountKind;
  institution?: string | null;
  nickname?: string | null;
  /** Exactly 4 digits. */
  last4?: string | null;
  accountNumber?: string | null;
}

/** 201 `{ created: true }`, or 200 `{ created: false }` when it was already on the roster. */
export interface CreateDdAccountResponse {
  account: DdAccount;
  created: boolean;
}

/** PATCH /rooms/:roomId/dd-accounts/:accountId — any subset. `accountNumber`
 * omitted keeps a stored number; null or '' clears it. 409 on a duplicate. */
export interface UpdateDdAccountRequest {
  institution?: string | null;
  nickname?: string | null;
  last4?: string | null;
  accountNumber?: string | null;
}

/** POST /rooms/:roomId/dd-accounts/:accountId/merge (admin only) → the survivor. */
export interface MergeDdAccountRequest {
  intoAccountId: string;
}

/** POST /rooms/:roomId/dd-accounts/:accountId/reveal-number — the FULL number,
 * audited server-side. Show it only on an explicit click; never keep or log it. */
export interface RevealDdAccountNumberResponse {
  accountNumber: string;
}

// ---------------------------------------------------------------------------
// Support (migration 0029) — minimal "Contact support" intake. One table, one
// create endpoint; no admin read/answer surface exists yet (deliberate
// follow-up). Mirrors src/modules/support + src/modules/http/routes/support.ts.
// ---------------------------------------------------------------------------

export type SupportRequestStatus = 'open';

/** POST /support-requests — file a support request as the signed-in actor.
 * `contextRef` is free text naming what prompted it (e.g. "reopen room
 * <room-id>"); never validated against a real record. */
export interface CreateSupportRequestRequest {
  subject: string;
  body: string;
  contextRef?: string;
}

/** A filed support request, as returned by POST /support-requests (201). */
export interface SupportRequest {
  id: string;
  tenantId: string;
  actorId: string;
  subject: string;
  body: string;
  contextRef: string | null;
  status: SupportRequestStatus;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Tenant admin roster (ADR-0013, migration 0030) — the tenant-wide role that
// gates room creation and (owner-only) managing this roster itself. Mirrors
// src/modules/tenancy/actors.ts Actor + src/modules/http/routes/tenant-admins.ts.
// All three endpoints are OWNER-ONLY server-side; a non-owner caller (including
// a rooms_admin) gets 403.
// ---------------------------------------------------------------------------

export type TenantRole = 'owner' | 'rooms_admin';

/**
 * An actor as the tenant-admin roster serializes it (GET /tenant/admins →
 * `{ admins }`; also the body of the grant/revoke responses). `tenantRole` is
 * always 'owner' or 'rooms_admin' on this roster (the endpoint only ever
 * returns/creates admins); the wider {@link Actor}-shaped field stays nullable
 * to mirror the backend column, which is null for every non-admin actor.
 */
export interface TenantAdmin {
  id: string;
  tenantId: string;
  email: string;
  displayName: string | null;
  emailVerifiedAt: string | null;
  tenantRole: TenantRole | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListTenantAdminsResponse {
  admins: TenantAdmin[];
  /** The signed-in owner's own actor id (ADR-0055), so the page can mark "you". */
  currentActorId?: string;
  /** Owner removals waiting out their 72 hours (ADR-0055, review MED-1). */
  pendingRemovals?: PendingOwnerRemoval[];
}

/** A pending removal of an owner: takes effect at `effectiveAt` unless cancelled. */
export interface PendingOwnerRemoval {
  id: string;
  targetActorId: string;
  requestedBy: string;
  requestedByEmail: string | null;
  requestedAt: string;
  effectiveAt: string;
}

/** POST / DELETE /tenant/admins/:actorId/owner (ADR-0055) — make a firm admin an
 * owner, or step an owner back down to firm admin. Owner-only (with MFA).
 * Stepping YOURSELF down is immediate; removing ANOTHER owner answers 202 with a
 * pending removal that takes effect after 72 hours unless cancelled.
 * `changed: false` when they already were / were not an owner. A 409 carries a
 * `code`: `last_owner` (a firm keeps at least one owner), `mfa_not_enrolled` /
 * `email_unverified` (the person must sign in and set up two-step sign-in first). */
export interface SetTenantOwnerResponse {
  actor: TenantAdmin;
  changed: boolean;
  /** Removing ANOTHER owner: the pending removal (202), else null. */
  pending?: Omit<PendingOwnerRemoval, 'requestedByEmail'> | null;
}

/** GET/PUT/DELETE /tenant/recovery-contact, POST /tenant/recovery-contact/confirm
 * (ADR-0055) — the firm's recovery contact, owner-only. PUT stores the address
 * as PENDING and emails it a 6-digit code (202); confirming with that code makes
 * it the contact. */
export interface RecoveryContact {
  /** The confirmed contact, or null. */
  email: string | null;
  confirmedAt: string | null;
  /** An address waiting for its emailed code, or null. */
  pendingEmail: string | null;
  pendingRequestedAt: string | null;
}

/** GET/PUT /tenant/classification-learning (ADR-0020 phase 3) — whether the
 * firm takes part in cross-customer classification learning: its filings count
 * toward, and it is shown, anonymized filing patterns agreed across customers.
 * On by default; owner-only. */
export interface ClassificationLearningSetting {
  shareWithAllCustomers: boolean;
}

/** POST /tenant/admins — grant the `rooms_admin` role to an actor by email
 * (owner-only). Idempotent (200) if already a rooms_admin; 409 if the email is
 * the tenant owner. Find-or-creates the actor in-tenant, exactly like the
 * seller-bind and invite-party flows. */
export interface GrantTenantAdminRequest {
  email: string;
}

/** DELETE /tenant/admins/:actorId — revoke a delegated admin back to a
 * non-admin actor (owner-only). 409 if the target is an owner (step them down
 * first); 404 if the actor is not currently an admin. */
export interface RevokeTenantAdminResponse {
  actor: TenantAdmin;
}

// ---------------------------------------------------------------------------
// Seller SECTION CERTIFICATIONS (migration 0038) — the "Certified" tick (🟢) on
// a DD checklist section, the seller-side companion to the AI "Classified" tick
// (🔵). The bound SELLER attests that a SECTION (a category within a phase) of
// the requested-documents checklist is resolved: 'complete' ("all documents for
// this section are uploaded") or 'not_applicable' ("this section does not apply
// to my business"). Every attestation is hash-chained into the audit_log
// server-side. Mirrors the serialized `SectionCertification`
// (src/modules/certification/index.ts) — `Date`s become ISO strings over JSON.
//
// `sectionKey` is OPAQUE to the backend; the UI forms it as `${phaseId}:${categoryTagId}`
// for a category section (see lib/section.ts `sectionKeyForCategory`), or
// `${phaseId}` for a phase-level / ungrouped bucket (`phaseSectionKey`).
// ---------------------------------------------------------------------------

/** The two valid attestation choices (mirrors the backend enum + DB CHECK). */
export type CertificationStatus = 'complete' | 'not_applicable';

/**
 * One section certification as the API serializes it (GET
 * /rooms/:roomId/certifications → `{ certifications }`; also the 200 body of the
 * certify POST). `certifiedBy` is the attesting actor id; `certifiedAt`/`updatedAt`
 * are ISO strings.
 */
export interface SectionCertification {
  id: string;
  roomId: string;
  sectionKey: string;
  status: CertificationStatus;
  /** The actor who made the current attestation (the bound seller). */
  certifiedBy: string;
  certifiedAt: string;
  note: string | null;
  updatedAt: string;
  /** True when the section changed after a 'complete' certification (a new
   * required item, an item due again, a document unlinked): it no longer counts
   * as certified until the seller certifies again. */
  needsRecertification?: boolean;
  invalidatedAt?: string | null;
  invalidatedReason?:
    | 'required_item_added'
    | 'required_item_due'
    | 'item_added'
    | 'item_due'
    | 'item_marked_not_applicable'
    | 'item_not_applicable_cleared'
    | 'document_unlinked'
    | null;
}

/** GET /rooms/:roomId/certifications → the room's CURRENT section certifications
 * in `certifications`, and the ones a later change invalidated in
 * `needsRecertification` (admin-or-seller read). */
export interface ListCertificationsResponse {
  certifications: SectionCertification[];
  needsRecertification?: SectionCertification[];
}

/**
 * POST /rooms/:roomId/sections/:sectionKey/certify — the bound SELLER attests a
 * section (admin → 404). `note` is an optional free-text attestation note.
 * DELETE on the same path withdraws it (no body).
 */
export interface CertifySectionRequest {
  status: CertificationStatus;
  note?: string;
}

/** 200 body of DELETE /rooms/:roomId/sections/:sectionKey/certify (withdraw). */
export interface WithdrawCertificationResponse {
  ok: true;
}

// ---------------------------------------------------------------------------
// Weekly due-diligence digest, risks & issues log, planned timeline (ADR-0047,
// migration 0066). Risks and milestones are facts a PERSON enters — the AI never
// adds, scores or judges them. Who reads an entry is its visibility ladder:
// deal_team (admin + deal team) ⊂ seller (+ the bound seller) ⊂ all (+ buyers).
// ---------------------------------------------------------------------------

export type RoomItemVisibility = 'deal_team' | 'seller' | 'all';
export type RiskSeverity = 'low' | 'medium' | 'high';
export type RiskStatus = 'open' | 'mitigating' | 'resolved';

/**
 * One risks & issues entry as GET /rooms/:roomId/risks serializes it. `owner` is
 * the display text for the caller. `ownerActorId`, `ownerLabel` and `visibility`
 * are the editable fields and come ONLY to the room admin (the seller's
 * projection drops them).
 */
export interface RoomRisk {
  id: string;
  title: string;
  description: string | null;
  severity: RiskSeverity;
  status: RiskStatus;
  /** 'YYYY-MM-DD' or null. */
  dueDate: string | null;
  owner: string | null;
  ownerActorId?: string | null;
  ownerLabel?: string | null;
  visibility?: RoomItemVisibility;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A person a risk may be assigned to: the room's deal-side participants only
 * (admin, deal team, bound seller) — never a buyer. */
export interface RiskOwnerOption {
  actorId: string;
  name: string;
  role: 'admin' | 'deal_team' | 'seller';
}

/** GET /rooms/:roomId/risks (admin or bound seller). `ownerOptions` is admin-only. */
export interface ListRisksResponse {
  risks: RoomRisk[];
  ownerOptions?: RiskOwnerOption[];
}

/** POST /rooms/:roomId/risks (admin) → 201 RoomRisk. The owner is a person
 * (`ownerActorId`) or free text (`ownerLabel`) — never both (400). */
export interface CreateRiskRequest {
  title: string;
  description?: string | null;
  severity: RiskSeverity;
  ownerActorId?: string | null;
  ownerLabel?: string | null;
  dueDate?: string | null;
  status?: RiskStatus;
  visibility?: RoomItemVisibility;
}

/** PATCH /rooms/:roomId/risks/:riskId (admin) → RoomRisk. Partial; closing an
 * entry is `{ status: 'resolved' }` (there is no delete). */
export type UpdateRiskRequest = Partial<CreateRiskRequest>;

/** An admin-entered timeline milestone ('YYYY-MM-DD'); `visibility` is admin-only. */
export interface RoomMilestone {
  id: string;
  name: string;
  date: string;
  visibility?: RoomItemVisibility;
}

/** GET /rooms/:roomId/milestones (admin or bound seller), by date. */
export interface ListMilestonesResponse {
  milestones: RoomMilestone[];
}

/** POST /rooms/:roomId/milestones (admin) → 201 RoomMilestone. */
export interface CreateMilestoneRequest {
  name: string;
  date: string;
  visibility?: RoomItemVisibility;
}

/** PATCH /rooms/:roomId/milestones/:milestoneId (admin). DELETE → 204. */
export type UpdateMilestoneRequest = Partial<CreateMilestoneRequest>;

/** The room's weekly digest settings (absent row = the defaults: on, Monday
 * 08:00 room time, admin + deal team + seller on, buyers off). */
export interface DigestSettings {
  enabled: boolean;
  /** ISO weekday: 1 = Monday … 7 = Sunday. */
  dayOfWeek: number;
  /** 0–23, in the room's timezone. */
  hourOfDay: number;
  sendToAdmin: boolean;
  sendToDealTeam: boolean;
  sendToSeller: boolean;
  sendToBuyers: boolean;
}

export type DigestAudience = 'admin' | 'deal_team' | 'seller' | 'buyer';

export interface DigestRecipient {
  actorId: string;
  name: string;
  email: string;
  audience: DigestAudience;
  /** The person used the unsubscribe link (the admin's settings never override it). */
  optedOut: boolean;
  /** Not the admin and has not confirmed their email address: the digest is not
   * sent to them until they do (ADR-0047). */
  unconfirmed: boolean;
  willReceive: boolean;
}

/** GET / PUT /rooms/:roomId/digest-settings (admin). PUT takes all seven
 * {@link DigestSettings} fields and answers with the same view. */
export interface DigestSettingsView {
  settings: DigestSettings;
  timeZone: string;
  /** ISO instant of the next weekly send, or null while the digest is off. */
  nextSendAt: string | null;
  recipients: DigestRecipient[];
}

/** GET /rooms/:roomId/digest/preview?as=admin|seller (admin). */
export interface DigestPreview {
  subject: string;
  html: string;
  text: string;
}

/** POST /rooms/:roomId/digest/preview (admin) → 202: the admin's own copy was emailed. */
export interface SendDigestPreviewResponse {
  sent: true;
}

// ---------------------------------------------------------------------------
// Contract expiration register (ADR-0054, migration 0074). The dates are the
// ones each document STATES — found by code and selected by the AI from that
// list (never generated); below the confidence floor a date reads "not found".
// Flags are plain date comparisons. A person (room admin / bound seller) can
// correct any field; a corrected field is never overwritten by a later read.
// ---------------------------------------------------------------------------

export type ContractType =
  | 'lease'
  | 'customer_agreement'
  | 'supplier_agreement'
  | 'insurance_policy'
  | 'license_permit'
  | 'loan_agreement'
  | 'employment_agreement'
  | 'other_agreement';

export type ContractFlag = 'expired' | 'expires_within_90_days' | 'expires_before_closing';

/** `pending`: the current version has not been read yet; `not_contract`: filed with
 * contracts but not the instrument itself (a statement, a list); `no_text`: no text
 * could be read from the file. */
export type ContractEntryStatus = 'extracted' | 'not_contract' | 'no_text' | 'pending';

export type ContractCorrectableField =
  | 'effective_date'
  | 'expiration_date'
  | 'notice_by'
  | 'auto_renew'
  | 'counterparty';

/** One row of GET /rooms/:roomId/contracts. Dates are 'YYYY-MM-DD' or null (not found). */
export interface ContractRegisterEntry {
  documentId: string;
  name: string | null;
  contractType: ContractType;
  status: ContractEntryStatus;
  /** The date the last party signed — when the contract was fully executed. */
  executionDate: string | null;
  effectiveDate: string | null;
  /** The effective date IS the execution date: the contract takes effect on signing. */
  effectiveFromExecution: boolean;
  expirationDate: string | null;
  noticeBy: string | null;
  noticePeriod: { count: number; unit: 'day' | 'month' } | null;
  autoRenew: boolean | null;
  counterparty: string | null;
  confidence: {
    executionDate: number | null;
    effectiveDate: number | null;
    expirationDate: number | null;
    notice: number | null;
    autoRenew: number | null;
    counterparty: number | null;
  };
  corrected: ContractCorrectableField[];
  flags: ContractFlag[];
}

/** The most recent P&L / balance sheet the caller can see, and how old its data is. */
export interface FinancialStatementFact {
  kind: string;
  latestDate: string;
  latestDocumentId: string;
  latestDocumentName: string | null;
  daysOld: number;
  stale: boolean;
}

/** GET /rooms/:roomId/contracts — over the caller's OWN visible documents. */
export interface ContractRegister {
  /** Room-local today the flags were computed against. */
  today: string;
  /** The expected closing date the caller may know, or null (no closing flag then). */
  expectedClosingDate: string | null;
  contracts: ContractRegisterEntry[];
  financialStatements: FinancialStatementFact[];
}

/** PATCH /rooms/:roomId/contracts/:documentId (admin / bound seller). Each field
 * present is set; null clears it to "not found". */
export interface CorrectContractRequest {
  effectiveDate?: string | null;
  expirationDate?: string | null;
  noticeBy?: string | null;
  autoRenew?: boolean | null;
  counterparty?: string | null;
}

export interface CorrectContractResponse {
  documentId: string;
  executionDate: string | null;
  effectiveDate: string | null;
  effectiveFromExecution: boolean;
  expirationDate: string | null;
  noticeBy: string | null;
  autoRenew: boolean | null;
  counterparty: string | null;
  corrected: ContractCorrectableField[];
}
